/*
 * PROJECT WEBROOM — Plan My Room page controller (ASP theme asset).
 *
 * Consumes the REAL ported engines on window.WebRoomEngine (bundled from
 * lib/webroom via esbuild — DO NOT reimplement the math here) and the baked
 * webroom catalog on window.PMR_CATALOG (real products only). Drives the
 * S0..S5 dimensions-first funnel, renders the 2D SVG payoff, and posts the
 * hand-raise to the origin-checked OH360 proxy on window.PMR_CONFIG.proxyUrl.
 *
 * No framework, no heavy libs. Mobile-first. Pure DOM.
 */
(function () {
  'use strict';

  var E = window.WebRoomEngine;
  var CFG = window.PMR_CONFIG || {};
  var PHONE = '(888) 923-5777';
  // Free Room Analysis: the expert-does-it-for-you path. The planner FEEDS it.
  var FRA_URL = CFG.fraUrl || '/pages/free-room-analysis';
  // ---- Slice 2.3 (large buy path, 2026-07-22) ----
  // Large rooms (>300 sqft floor -> the engine's large-panel rule) can now buy the
  // real 48x96 large-format plan in-flow, instead of only routing to FRA. Static,
  // Dan-reviewed freight copy (freight wording emailed to Dan 2026-07-19, silence =
  // stands): dock-to-dock is the base delivery; a liftgate is an ADD-ON. No lead-time
  // promises, no "free shipping", no liftgate-included claim (kept out of dynamic
  // strings per the plan's legal-sensitivity note).
  var FREIGHT_NOTE = 'Large-format panels ship by freight, dock to dock. A liftgate is available as an add-on for locations without a loading dock.';
  // The ASP theme cart enforces a 5-piece large-format freight combine pool
  // (snippets/asp-cart-min-qty.liquid, enforce.combine_pool). Below it, checkout
  // blocks; so the large buy path is offered only at/above this count and steers to
  // Send My Plan otherwise (never a cart that errors at checkout).
  var LARGE_FREIGHT_MIN = 5;
  // LAUNCH FLAG (2026-07-13): the Room Visualizer iOS app is NOT yet live on the App
  // Store (ASC app id 6763380169; submission WAITING_FOR_REVIEW as of 2026-07-13).
  // CFG.appStoreUrl is empty in the template config until launch, so every app mention
  // below (S5 upsell card + the dims-screen one-liner) is gated on APP_STORE_URL and
  // stays hidden. TO FLIP AT LAUNCH: set "appStoreUrl" to the live App Store URL in
  // templates/page.plan-my-room.liquid's #pmr-config JSON. That single template edit
  // turns both mentions on across the whole flow. No JS change, no theme redeploy.
  var APP_STORE_URL = CFG.appStoreUrl || '';
  var CATALOG = null; // decoded live v:2 island (CatalogProduct[]) — set in boot()
  var FCD = null;     // fabric-color-data.json { tiles, colors[] } — PDP swatch source

  // ---- Exit routing config (evidence-derived from the close-data analysis: 3,617 orders + the
  //      OH360 proposal path, 2026-07-13). ALL THREE exits always render; this only re-ranks them
  //      and moves the single RECOMMENDED badge. It NEVER hides the cart. Physical rules decide
  //      first (they override dollars); the dollar bands are the fallback. Do NOT "round" these
  //      numbers back to tidy values without re-reading the provenance note on each line. ----
  var ROUTING = {
    // physical / scale signals (override the dollar bands)
    largeSqft: 300,          // coverage sqft >= 300 -> FRA. Evidence: 300+ sqft is 84% human-assisted, median order $5,366.
    midSqftFloor: 100,       // coverage sqft in [100,300) and not freight -> Send. Mid rooms self-serve or hand-raise; rarely need FRA.
    freightIsLarge: true,    // a freight / large-format job -> FRA. Evidence: freight orders are 19:1 human-assisted.
    freightLongDimIn: 48,    // a spec'd panel whose long dimension exceeds this (in) is freight. NOTE: the planner displays the
                             // standard 24x48 parcel panel (long dim exactly 48, never > 48), so this never trips on the display
                             // panel; the true large-format / freight case is carried by the engine via plan.isLarge.
    // dollar fallback (used only when the physical signals do not decide)
    smallMax: 2000,          // < $2,000 -> Cart. Evidence: self-serve p90 is $1,498; web beats assisted in every band up to $2K.
                             // (NOT 1000 — the provisional would have buried proven self-serve demand.)
    midMax: 5000,            // $2,000..$5,000 -> Send; > $5,000 -> FRA. Evidence: $3-5K still 22% genuine self-serve;
                             // freight/sqft overrides catch the true FRA cases. (NOT 3000.)
    smallMaxCommercial: 3000 // if a company name is known, raise the Cart ceiling to this. Evidence: commercial buyers
                             // self-serve at ~2x residential, p90 ~3x.
  };

  // The fabric LINES ASP sells, each mapped to the parity engine's FabricLine id.
  // `lines` lets the Guilford tile aggregate its three sub-lines. Which tiles
  // actually render is derived from the LIVE catalog per size (availableTiles()).
  var FABRIC_TILES = [
    { key: 'burch',      label: 'Burch Prime Time', line: 'burchPrimeTime', lines: ['burchPrimeTime'], note: 'Made in USA, Class A' },
    { key: 'microsuede', label: 'Microsuede',       line: 'microsuede',     lines: ['microsuede'],     note: 'Soft suede feel' },
    { key: 'burlap',     label: 'Burlap',           line: 'burlap',         lines: ['burlap'],         note: 'Natural texture' },
    { key: 'guilford',   label: 'Guilford of Maine', line: 'guilfordFR701', lines: ['guilfordFR701', 'guilfordAnchorage', 'guilfordWhisper'], note: 'FR701 / Anchorage / Whisper' }
  ];

  // Last-resort colors if neither the live catalog nor the PDP swatch feed loaded
  // (the page still works). Real Burch names; no image -> neutral chip.
  var FALLBACK_COLORS = [
    { name: 'Jet Black', line: 'burchPrimeTime' },
    { name: 'Granite', line: 'burchPrimeTime' },
    { name: 'Chambray', line: 'burchPrimeTime' },
    { name: 'Parchment', line: 'burchPrimeTime' },
    { name: 'Marina', line: 'burchPrimeTime' }
  ];

  // Map a raw fabric/Material name (catalog or PDP feed) to a parity FabricLine.
  function lineFromFabricName(f) {
    var s = String(f || '').toLowerCase();
    if (s.indexOf('burch') >= 0) return 'burchPrimeTime';
    if (s.indexOf('microsuede') >= 0) return 'microsuede';
    if (s.indexOf('burlap') >= 0) return 'burlap';
    if (s.indexOf('anchorage') >= 0) return 'guilfordAnchorage';
    if (s.indexOf('whisper') >= 0) return 'guilfordWhisper';
    if (s.indexOf('guilford') >= 0) return 'guilfordFR701';
    return 'burchPrimeTime';
  }
  function tileFromLine(line) {
    if (line === 'microsuede') return 'microsuede';
    if (line === 'burlap') return 'burlap';
    if (String(line).indexOf('guilford') === 0) return 'guilford';
    return 'burch';
  }
  function tileByKey(k) {
    for (var i = 0; i < FABRIC_TILES.length; i++) { if (FABRIC_TILES[i].key === k) return FABRIC_TILES[i]; }
    return FABRIC_TILES[0];
  }

  if (!E || !E.planRoom || !E.decodeCatalog) {
    console.error('[plan-my-room] engine bundle missing planRoom/decodeCatalog');
    return;
  }

  // Min/max REAL catalog price (cents) for a size, for the modeled price range.
  function sizeBand(w, h, t) {
    if (!CATALOG) return null;
    var ps = [];
    CATALOG.forEach(function (p) {
      p.variants.forEach(function (v) {
        if (Math.abs(v.widthInches - w) < 0.6 && Math.abs(v.heightInches - h) < 0.6 && Math.abs(v.thicknessInches - t) < 0.6) ps.push(v.priceCents);
      });
    });
    if (!ps.length) return null;
    return [Math.min.apply(null, ps), Math.max.apply(null, ps)];
  }

  // ---- Fabric line + color sourcing (real data; the MONEY path still resolves
  // through the engine's resolveVariant via planRoom, never a hardcoded palette) ----

  // Size-tolerant dim match + color-name portioning (UI list building only).
  function dimsMatchUI(v, w, h, t) {
    var sizeOK = (Math.abs(v.widthInches - w) < 0.6 && Math.abs(v.heightInches - h) < 0.6) ||
                 (Math.abs(v.widthInches - h) < 0.6 && Math.abs(v.heightInches - w) < 0.6);
    return sizeOK && Math.abs(v.thicknessInches - t) < 0.6;
  }
  function colorPortionUI(raw) {
    if (raw == null) return '';
    var s = String(raw);
    var idx = s.lastIndexOf(': ');
    return idx >= 0 ? s.slice(idx + 2) : s;
  }

  // Color-name normalization. Catalog color names drift by size ("Natural / Camel"
  // vs "Camel", "Ivory (Off White)" vs "Ivory (Natural)" vs "Ivory") while the PDP
  // swatch feed (FCD) carries its own canonical names. Reduce both to candidate
  // keys so a name matches its image regardless of parentheticals, slashes or case.
  function normColor(s) { return String(s == null ? '' : s).toLowerCase().replace(/\s+/g, ' ').trim(); }
  function colorKeys(name) {
    var base = normColor(name), keys = {};
    if (base) keys[base] = 1;
    var noParen = base.replace(/\s*\([^)]*\)\s*/g, ' ').replace(/\s+/g, ' ').trim(); // "ivory (off white)" -> "ivory"
    if (noParen) keys[noParen] = 1;
    var m = base.match(/\(([^)]*)\)/); if (m && m[1].trim()) keys[normColor(m[1])] = 1; // inner: "off white"
    [base, noParen].forEach(function (str) {                                            // "natural / camel" -> "natural","camel"
      if (str.indexOf('/') >= 0) { str.split('/').forEach(function (tok) { tok = tok.trim(); if (tok) keys[tok] = 1; }); }
    });
    return Object.keys(keys);
  }

  // FCD swatch index: { tile: { candidateKey: imageUrl } }. Built once per FCD load.
  var _fcdIdx = null;
  function fcdIndex() {
    if (_fcdIdx) return _fcdIdx;
    if (!FCD || !FCD.colors) return {}; // FCD not loaded yet -> empty, do NOT cache
    _fcdIdx = {};
    FCD.colors.forEach(function (c) {
      if (!c.tile || !c.name || !c.image) return;
      var bucket = (_fcdIdx[c.tile] = _fcdIdx[c.tile] || {});
      colorKeys(c.name).forEach(function (k) { if (!bucket[k]) bucket[k] = c.image; });
    });
    return _fcdIdx;
  }

  // Representative RGB chips for colors that genuinely have NO image in the feed
  // (e.g. Microsuede Chocolate, Burlap Blue). Never a blank gray square — always a
  // plausible, named chip. Keyed by normalized color word; candidate keys are tried.
  var COLOR_HEX = {
    'black': '#1c1c1c', 'jet black': '#1c1c1c', 'onyx': '#1c1c1c',
    'white': '#f4f1ea', 'off white': '#efe9dd', 'ivory': '#efe9dd', 'natural': '#e6dcc6', 'parchment': '#e9e2d0', 'oatmeal': '#e2d8c4',
    'gray': '#8a8a8a', 'grey': '#8a8a8a', 'granite': '#6f7276', 'shale': '#6b6b66', 'shark': '#7d8085', 'cinder': '#4d4d4f', 'elephant': '#8f8d88', 'silver': '#b6b6b6', 'pewter': '#8a8c8f',
    'camel': '#c9a877', 'buckwheat': '#c9a877', 'tan': '#c9a877', 'beige': '#d8c6a5', 'khaki': '#bfae87', 'wheat': '#d6c19a',
    'chocolate': '#4a3327', 'brown': '#5a4230', 'espresso': '#3d2b22', 'coffee': '#4a3327', 'mocha': '#6a4a38', 'walnut': '#5a4230',
    'red': '#a11f24', 'chinese red': '#b21f24', 'crimson': '#9b1c22', 'brick': '#8f3a30', 'burgundy': '#6d1f2b',
    'blue': '#3f5a86', 'navy': '#26344f', 'chambray': '#8ba0b8', 'baltic': '#31597a', 'denim': '#47597a', 'slate': '#4a5a6a', 'marina': '#2f5f8f', 'sky': '#7fa6c8', 'teal': '#2f6f6a',
    'sage': '#9aa87f', 'green': '#4a6b4a', 'olive': '#6b6a3a', 'forest': '#2f4a30', 'moss': '#6a7248',
    'gold': '#c6a24a', 'yellow': '#d8be5a', 'amber': '#c58f3a', 'mustard': '#c39a3a',
    'purple': '#5a4a7a', 'amethyst': '#6f5a8f', 'plum': '#5a3a4a', 'violet': '#5a4a7a',
    'pink': '#c98a9a', 'rose': '#b56a72', 'mauve': '#a5808f',
    'orange': '#c8703a', 'rust': '#9a5a34', 'terracotta': '#a5603f', 'copper': '#a5603f'
  };
  function colorChipHex(name) {
    var ks = colorKeys(name);
    for (var i = 0; i < ks.length; i++) { if (COLOR_HEX[ks[i]]) return COLOR_HEX[ks[i]]; }
    // last resort: match on any single word token inside a candidate key
    for (var j = 0; j < ks.length; j++) {
      var words = ks[j].split(' ');
      for (var w = 0; w < words.length; w++) { if (COLOR_HEX[words[w]]) return COLOR_HEX[words[w]]; }
    }
    return null;
  }

  // Real swatch image for a (line, colorName) from the PDP feed; null -> hex chip.
  function swatchImage(line, name) {
    if (!FCD || !FCD.colors) return null;
    var tk = tileFromLine(line);
    var idx = fcdIndex()[tk] || {};
    var ks = colorKeys(name);
    for (var i = 0; i < ks.length; i++) { if (idx[ks[i]]) return idx[ks[i]]; }
    return null;
  }

  // Palette colors (PDP feed) for a tile -> [{name,line,image}]. Used for
  // placeholder lines (2" Burch/Guilford) that have no discrete per-color variant.
  function paletteColorsFor(tile) {
    if (!FCD || !FCD.colors) return [];
    var out = [], seen = {};
    for (var i = 0; i < FCD.colors.length; i++) {
      var c = FCD.colors[i];
      if (c.tile !== tile.key || !c.name) continue;
      var key = String(c.name).toLowerCase();
      if (seen[key]) continue; seen[key] = 1;
      out.push({ name: c.name, line: lineFromFabricName(c.fabric), image: c.image || null });
    }
    return out;
  }

  // Named colors to show for a fabric tile at a panel size. Discrete lines
  // (Microsuede/Burlap, and 4" Burch/Guilford) come from the LIVE catalog so the
  // chosen name resolves to the exact orderable variant; placeholder lines (2"
  // Burch/Guilford = "ENTER FABRIC COLOR BELOW") come from the PDP swatch feed and
  // ride as a cart property. Never an invented color.
  function colorsForTile(tile, w, h, t) {
    var variants = [];
    if (CATALOG) {
      CATALOG.forEach(function (p) {
        p.variants.forEach(function (v) {
          if (!dimsMatchUI(v, w, h, t)) return;
          for (var li = 0; li < tile.lines.length; li++) {
            if (E.fabricMatches(tile.lines[li], v.fabricOptionValue)) { variants.push(v); break; }
          }
        });
      });
    }
    var discrete = [], seen = {};
    variants.forEach(function (v) {
      if (E.isPlaceholderColor(v)) return;
      var nm = colorPortionUI(v.color); if (!nm) return;
      var line = lineFromFabricName(v.fabricOptionValue);
      var key = line + '|' + nm.toLowerCase();
      if (seen[key]) return; seen[key] = 1;
      discrete.push({ name: nm, line: line, image: swatchImage(line, nm) });
    });
    if (discrete.length > 0) return discrete;   // real orderable variants
    var palette = paletteColorsFor(tile);        // placeholder line -> PDP palette
    if (palette.length > 0) return palette;
    return FALLBACK_COLORS.slice();              // last resort
  }

  // Tiles to show at this size + their "from $X" (live). Only tiles with a live
  // variant at the size render (falls back to Burch-only before the catalog loads).
  function availableTiles(w, h, t) {
    return FABRIC_TILES.map(function (tile) {
      var from = null;
      if (CATALOG) {
        for (var li = 0; li < tile.lines.length; li++) {
          var c = E.fromPriceCents(CATALOG, { widthInches: w, heightInches: h, thicknessInches: t, fabric: tile.lines[li] });
          if (c != null) from = (from == null) ? c : Math.min(from, c);
        }
      }
      return { tile: tile, fromCents: from, available: CATALOG ? (from != null) : (tile.key === 'burch') };
    }).filter(function (o) { return o.available; });
  }

  // Resolve the current fabric line + color name (+ the display list) for the
  // selected tile at a panel size. Keeps a prior selection sticky across renders.
  function currentFabric(w, h, t) {
    var tile = tileByKey(st.tileKey);
    var colors = colorsForTile(tile, w, h, t);
    // Slice 3.1: a shared link carries the color as a SLUG, and the catalog it
    // has to be matched against loads async — so at restore time the real color
    // object does not exist yet. Resolve on the first render where the color
    // list is actually populated, then drop the slug (a color that no longer
    // exists silently falls back to the tile default, never a broken selection).
    if (st._sharedColor && colors.length) {
      for (var ci = 0; ci < colors.length; ci++) {
        if (shareSlug(colors[ci].name) === st._sharedColor) { st.color = colors[ci]; break; }
      }
      st._sharedColor = null;
    }
    var sel = null;
    if (st.color) {
      for (var i = 0; i < colors.length; i++) {
        if (colors[i].name === st.color.name && colors[i].line === st.color.line) { sel = colors[i]; break; }
      }
      if (!sel) sel = st.color;
    }
    if (!sel) sel = colors[0] || { name: 'Black', line: tile.line };
    return { tile: tile, colors: colors, sel: sel };
  }

  // ---- Room-type ghost defaults (typical L x W x H ft) + engine SpaceType map ----
  var ROOM_TYPES = [
    { key: 'Home Theater', label: 'Home Theater', ic: '🎥', def: [20, 15, 9] },
    { key: 'Studio', label: 'Studio', ic: '🎤', def: [16, 13, 9] },
    { key: 'Office', label: 'Office', ic: '💼', def: [15, 12, 9] },
    { key: 'Restaurant', label: 'Restaurant', ic: '🍽', def: [40, 30, 11] },
    { key: 'Church', label: 'Church', ic: '⛪', def: [60, 40, 20] },
    { key: 'Gym / Fitness', label: 'Gym / Fitness', ic: '🏋', def: [50, 40, 16] },
    { key: 'Classroom', label: 'Classroom', ic: '🎓', def: [30, 24, 10] },
    { key: 'Other', label: 'Other', ic: '…', def: [18, 14, 9] }
  ];
  function typeByKey(k) {
    if (!k) return null;
    var s = String(k).toLowerCase();
    for (var i = 0; i < ROOM_TYPES.length; i++) {
      var t = ROOM_TYPES[i];
      if (t.key.toLowerCase() === s || t.label.toLowerCase() === s) return t;
      // tolerant deep-link aliases (?type=gym, ?type=home-theater, ?type=church)
      var alias = t.key.toLowerCase().replace(/[^a-z]/g, '');
      if (alias.indexOf(s.replace(/[^a-z]/g, '')) === 0 && s.length > 2) return t;
    }
    return null;
  }

  // ---- State ----
  var st = {
    step: 0,
    space: null,        // ROOM_TYPES entry
    mode: 'dims',       // 'dims' | 'photos'
    L: 18, W: 14, H: 9,
    tier: 'Reference',
    thickness: '2',
    tileKey: 'burch',   // selected fabric-line tile
    color: null,        // selected { name, line, image? } — null = tile default
    photos: {},         // { 'Wall 1': File, ... }
    plan: null,
    sent: false,        // first Send My Plan happened -> enables submit:false syncs
    contactId: null,
    dealId: null,
    formLoadedAt: Date.now(),
    help: false,
    // ---- Phase 2 (2026-07-19): surface picker + per-wall adjust + problem chips ----
    problem: null,      // selected PROBLEMS key or null
    skipWalls: {},      // { w0: true } walls marked "skip" (windows, brick, doors)
    ceilingOn: true,    // ceiling toggle (engine includeCeiling; ASP default on)
    wallCounts: {},     // absolute per-wall count overrides { w0: 5 } (customer +/-)
    ceilCount: null,    // explicit ceiling count override (null = automatic)
    selWall: null,      // layout-card tab: wallId | 'ceiling' | null (auto-pick)
    // ---- Slice 3.1 (share/save plan link, 2026-08-01) ----
    _sharedColor: null  // color slug pending resolution against the async catalog
  };

  // Problem chips (W1). Bias only ever UPGRADES the space type's recommended
  // tier (Comfort < Reference < Studio), never downgrades it. UI-only mapping;
  // the engine's tier override does the rest.
  var PROBLEMS = [
    { key: 'echo',   label: 'Echo / ringing',    bias: null },
    { key: 'speech', label: 'Speech is muddy',   bias: 'Reference' },
    { key: 'loud',   label: 'Too loud',          bias: 'Reference' },
    { key: 'rec',    label: 'Recording quality', bias: 'Studio' }
  ];
  var TIER_ORDER = { Comfort: 0, Reference: 1, Studio: 2 };
  function problemByKey(k) {
    for (var i = 0; i < PROBLEMS.length; i++) { if (PROBLEMS[i].key === k) return PROBLEMS[i]; }
    return null;
  }
  function tierWithProblemBias() {
    var base = st.space ? E.recommendedTier(st.space.key) : 'Reference';
    var pr = problemByKey(st.problem);
    if (pr && pr.bias && TIER_ORDER[pr.bias] > TIER_ORDER[base]) return pr.bias;
    return base;
  }
  // Room edits invalidate customer count overrides (different room, stale counts).
  function resetAdjustments() { st.wallCounts = {}; st.ceilCount = null; st.selWall = null; }

  // ======================= SLICE 3.1: SHARE / SAVE PLAN LINK =======================
  // The person who plans the room is very often not the person who approves the
  // spend (studio partner, facilities manager, church committee, spouse). Before
  // this, handing the plan over meant re-entering every input on their phone.
  // Now the whole plan serializes into the URL: ?p=<token> reopens the exact same
  // room, tier, fabric and surface edits on any device. No account, no server
  // round-trip, nothing stored.
  //
  // The codec is the ENGINE's (lib/webroom/share.ts, unit-tested) — this file only
  // maps planner state to/from it. Every engine call below is feature-detected so
  // a browser holding a cached PRE-3.1 engine bundle degrades to the old flow
  // instead of throwing (the controller and the engine are two separate theme
  // assets with independent CDN cache lifetimes).
  function shareReady() { return !!(E && typeof E.encodeSharedPlan === 'function' && typeof E.decodeSharedPlan === 'function'); }
  function shareSlug(v) {
    if (E && typeof E.slugify === 'function') return E.slugify(v);
    return String(v == null ? '' : v).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }

  // Planner state -> the engine's SharedPlan. Room inputs ONLY: no name, email,
  // phone, zip, contactId, dealId or photos ever enter a share link.
  function sharedPlanFromState() {
    var color = (st.plan && st.plan.color) ? st.plan.color : st.color;
    return {
      spaceType: st.space ? st.space.key : 'Other',
      lengthFt: st.L, widthFt: st.W, heightFt: st.H,
      tier: st.tier,
      ceilingOn: st.ceilingOn !== false,
      fabricKey: st.tileKey,
      colorSlug: color ? color.name : undefined,
      problem: st.problem || undefined,
      skipWallIds: skippedWallIds(),
      perWallCounts: st.wallCounts,
      ceilingCount: st.ceilCount
    };
  }

  // SharedPlan -> planner state. selectSpace() runs FIRST because it resets the
  // whole Phase-2 block (surfaces, adjustments, problem, tier) to that space
  // type's defaults; every field the link actually carries is applied after it.
  function applySharedPlan(sp) {
    selectSpace(sp.spaceType);
    st.L = sp.lengthFt; st.W = sp.widthFt; st.H = sp.heightFt;
    st.tier = sp.tier || st.tier;
    st.ceilingOn = sp.ceilingOn !== false;
    // Theme vocabulary is validated HERE (the engine only shape-checks the slug):
    // a fabric line or problem chip that has since been retired is dropped, not
    // forced into the UI.
    if (sp.problem && problemByKey(sp.problem)) st.problem = sp.problem;
    var tile = sp.fabricKey ? tileByKey(sp.fabricKey) : null;
    if (tile) st.tileKey = tile.key;
    st.color = null;
    st._sharedColor = sp.colorSlug || null; // resolved in currentFabric once the catalog lands
    st.skipWalls = {};
    (sp.skipWallIds || []).forEach(function (id) { st.skipWalls[id] = true; });
    st.wallCounts = {};
    Object.keys(sp.perWallCounts || {}).forEach(function (id) { st.wallCounts[id] = sp.perWallCounts[id]; });
    st.ceilCount = (sp.ceilingCount == null) ? null : sp.ceilingCount;
    st.selWall = null;
  }

  // Mint a link for the plan as it stands right now and hand it off. Native share
  // sheet on mobile (where "send this to my partner" actually happens), clipboard
  // on desktop, prompt as the last resort so the link is never unreachable.
  function sharePlan(btn) {
    if (!shareReady()) return;
    var url;
    try { url = E.buildShareUrl(window.location.href, sharedPlanFromState()); }
    catch (e) { return; }
    try { if (typeof gtag === 'function') { gtag('event', 'plan_share', { source: 'planner', step: st.step }); } } catch (e2) {}

    function confirmCopied() {
      if (!btn) return;
      var was = btn.textContent;
      btn.textContent = 'Link copied. It opens this exact plan.';
      setTimeout(function () { if (btn) btn.textContent = was; }, 3000);
    }
    if (navigator.share) {
      navigator.share({ title: 'My acoustic panel plan', url: url }).catch(function () {});
      return;
    }
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).then(confirmCopied).catch(function () { window.prompt('Copy this link to your plan:', url); });
      return;
    }
    window.prompt('Copy this link to your plan:', url);
  }

  // ---- Small DOM helpers ----
  function $(sel, root) { return (root || document).querySelector(sel); }
  function $all(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }
  function el(tag, cls, html) { var n = document.createElement(tag); if (cls) n.className = cls; if (html != null) n.innerHTML = html; return n; }
  function money(cents) { return '$' + (Math.round(cents) / 100).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }); }
  function clampNum(v, lo, hi) { v = Number(v) || 0; return v < lo ? lo : v > hi ? hi : v; }

  var ROOT = document.getElementById('pmr-app');
  if (!ROOT) { console.error('[plan-my-room] #pmr-app missing'); return; }

  // ---- Compute the plan via the parity-proven engine façade (DO NOT fork) ----
  // Phase 1 shows the STANDARD-panel plan (forcePanelSize:'standard') so the
  // headline count + RT60 match the Swift parity anchors exactly (Dan 33 / bedroom
  // 14 / restaurant 41). When the >300 sqft rule fires we surface it as a note and
  // let the specialist spec large-format — the mockup behavior.
  // Phase 2: current skip-list ('w0'..'w3') derived from the surface picker.
  function skippedWallIds() {
    return Object.keys(st.skipWalls).filter(function (k) { return st.skipWalls[k]; });
  }

  function computePlan() {
    var space = st.space ? st.space.key : 'Other';
    var TIN = 2; // planner ships the standard 2" plan (engine panelProfile default)
    var cf = currentFabric(24, 48, TIN);
    st.tileKey = cf.tile.key; // keep tile state in sync with what is shown

    // Phase 2 layout options: only pass `layout` when the customer actually
    // deviated from the defaults, so the untouched flow runs the parity-proven
    // Phase-1 path byte-for-byte.
    var skips = skippedWallIds();
    var hasCounts = Object.keys(st.wallCounts).length > 0 || st.ceilCount != null;
    var layout;
    if (skips.length || hasCounts) {
      layout = {};
      if (skips.length) layout.excludeWallIds = skips;
      if (Object.keys(st.wallCounts).length) layout.perWallCounts = st.wallCounts;
      if (st.ceilCount != null) layout.ceilingCount = st.ceilCount;
    }

    var r = E.planRoom({
      lengthFt: st.L, widthFt: st.W, heightFt: st.H, spaceType: space,
      options: { tier: st.tier, forcePanelSize: 'standard', includeCeiling: st.ceilingOn },
      pricing: CATALOG ? { catalog: CATALOG, fabric: cf.sel.line, colorName: cf.sel.name } : undefined,
      layout: layout
    });
    var rec = r.recommendation;
    // Adjusted plans price/count what is actually PLACED (engine purchaseCount);
    // the untouched flow keeps the Phase-1 recommended count.
    var adjusted = !!layout;
    var count = adjusted && r.purchaseCount != null ? r.purchaseCount : rec.recommendedPanelCount;
    var totalSqFt = rec.coverage.totalSurfaceSqFt;
    var coveragePct = totalSqFt > 0 ? Math.round((count * rec.panelFaceSqFt / totalSqFt) * 100) : 0;

    var band = sizeBand(24, 48, TIN);
    var unitCents = r.price ? r.price.unit.priceCents : 11999;
    var pricePoint = r.price ? r.price.totalCents : unitCents * count;
    var priceLow = (band ? band[0] : unitCents) * count;
    var priceHigh = (band ? band[1] : unitCents) * count;

    var rep = rec.acoustics; // AcousticReport for the displayed (standard) plan
    var reduction = rep.rt60BeforeMid > 0
      ? Math.max(0, Math.min(95, Math.round(((rep.rt60BeforeMid - rep.rt60AfterMid) / rep.rt60BeforeMid) * 100)))
      : 0;

    // Phase 2: per-wall picture for the interactive layout card. Capacity uses
    // the ENGINE's band math (wallBandCapacity) so +/- bounds and the ghost
    // affordance can never disagree with what arrange() will actually place.
    var planes = E.wallPlanes(r.room);
    var PANEL_M = { widthMeters: 24 * 0.0254, heightMeters: 48 * 0.0254 };
    var wallInfo = planes.slice().sort(function (a, b) { return a.index - b.index; }).map(function (pl) {
      var n = r.placement.filter(function (q) { return q.mount === 'wall' && !q.preserved && q.wallId === pl.wallId; }).length;
      return {
        id: pl.wallId, index: pl.index,
        lengthFt: Math.round(pl.length * 3.28084 * 10) / 10,
        count: n,
        cap: E.wallBandCapacity ? E.wallBandCapacity(pl, PANEL_M) : n,
        skipped: !!st.skipWalls[pl.wallId]
      };
    });
    var ceilingPlaced = r.placement.filter(function (q) { return q.mount === 'ceiling' && !q.preserved; }).length;
    var ceilingCap = E.ceilingGridCapacity ? E.ceilingGridCapacity(r.room, PANEL_M) : ceilingPlaced;
    // Coverage floor: the tier's minimum honest coverage (range[0]) in panels.
    var floorCount = rec.coverageRange && totalSqFt > 0
      ? Math.ceil((rec.coverageRange[0] * totalSqFt) / rec.panelFaceSqFt)
      : 0;

    // ---- Slice 2.3: buyable large-format (48x96) plan ----
    // When the >300 sqft large-panel rule fires, resolve the ACTUAL large-format
    // plan (fewer, bigger panels) alongside the parity-anchored standard display
    // plan. Additive: the standard plan above (count/layout/RT60/stats) is
    // untouched. The large plan is the recommended auto layout (a "buy the whole
    // large plan" action), so it does not carry the surface-picker overrides.
    var large = null;
    if (rec.largePanelRule.triggered) {
      var lr = E.planRoom({
        lengthFt: st.L, widthFt: st.W, heightFt: st.H, spaceType: space,
        options: { tier: st.tier, forcePanelSize: 'large', includeCeiling: st.ceilingOn },
        pricing: CATALOG ? { catalog: CATALOG, fabric: cf.sel.line, colorName: cf.sel.name } : undefined
      });
      var lrec = lr.recommendation;
      var lunit = lr.price ? lr.price.unit.priceCents : 0;
      large = {
        count: lrec.recommendedPanelCount,
        sizeLabel: '48" x 96" x ' + TIN + '"',
        panelFaceSqFt: lrec.panelFaceSqFt,
        unitCents: lunit,
        pricePoint: lr.price ? lr.price.totalCents : lunit * lrec.recommendedPanelCount,
        fromCents: lr.price ? lr.price.fromCents : null,
        variant: (lr.price && lr.price.unit.variant) ? lr.price.unit.variant : null,
        cartAttributes: lr.price ? lr.price.unit.cartAttributes : [],
        isPurchasable: !!(lr.price && lr.price.unit.isPurchasable)
      };
    }

    st.plan = {
      room: r.room, count: count, coveragePct: coveragePct,
      panelFaceSqFt: rec.panelFaceSqFt, sizeLabel: '24" x 48" x ' + TIN + '"', isLarge: rec.largePanelRule.triggered,
      priceLow: priceLow, priceHigh: priceHigh, pricePoint: pricePoint, unitCents: unitCents,
      color: cf.sel, fabricLine: cf.sel.line, fabricLabel: cf.tile.label, colors: cf.colors,
      rep: rep, reduction: reduction, profile: rec.panelProfile,
      placed: r.placement, planes: planes, pw: 24, ph: 48,
      wallSqFt: rec.coverage.wallAreaSqFt, ceilingSqFt: rec.coverage.ceilingAreaSqFt, totalSqFt: totalSqFt,
      variant: r.price && r.price.unit.variant ? r.price.unit.variant : null,
      cartAttributes: r.price ? r.price.unit.cartAttributes : [],
      isPurchasable: r.price ? r.price.unit.isPurchasable : false,
      tier: st.tier,
      // Phase 2 additions
      adjusted: adjusted,
      recommendedCount: rec.recommendedPanelCount,
      overflow: !!r.overflow,
      wallInfo: wallInfo,
      ceilingCount: ceilingPlaced,
      ceilingCap: ceilingCap,
      floorCount: floorCount,
      // Slice 2.3: buyable large-format plan (null unless the >300 sqft rule fired)
      large: large
    };
    return st.plan;
  }

  // ---- SVG: RT60 before/after bar chart ----
  function rt60Svg(rep) {
    var bands = rep.bands;
    var maxRt = 0.5;
    for (var i = 0; i < bands.length; i++) { maxRt = Math.max(maxRt, bands[i].rt60Before, bands[i].rt60After); }
    maxRt = Math.ceil(maxRt * 2) / 2; // round up to 0.5
    var W = 300, H = 130, base = 104, top = 10, left = 34, right = 296;
    var span = right - left;
    var group = (span) / bands.length;
    var barW = 12;
    function y(v) { return base - (v / maxRt) * (base - top); }
    var s = '<svg viewBox="0 0 300 130" width="100%" height="130" role="img" aria-label="RT60 before and after by frequency band">';
    s += '<line x1="34" y1="10" x2="34" y2="104" stroke="#e4e4e4"/><line x1="34" y1="104" x2="296" y2="104" stroke="#e4e4e4"/>';
    s += '<text x="6" y="' + (top + 8) + '" font-size="9" fill="#9a9a9a">' + maxRt.toFixed(1) + 's</text>';
    s += '<text x="6" y="' + (y(maxRt / 2) + 3) + '" font-size="9" fill="#9a9a9a">' + (maxRt / 2).toFixed(1) + 's</text>';
    s += '<text x="6" y="104" font-size="9" fill="#9a9a9a">0s</text>';
    for (var b = 0; b < bands.length; b++) {
      var cx = left + 10 + b * group;
      var yb = y(bands[b].rt60Before), ya = y(bands[b].rt60After);
      s += '<rect x="' + cx + '" y="' + yb + '" width="' + barW + '" height="' + (base - yb) + '" fill="#cfcfcf"/>';
      s += '<rect x="' + (cx + barW + 2) + '" y="' + ya + '" width="' + barW + '" height="' + (base - ya) + '" fill="#ea0606"/>';
      s += '<text x="' + (cx + 2) + '" y="116" font-size="8" fill="#9a9a9a">' + bands[b].label + '</text>';
    }
    s += '</svg>';
    return s;
  }

  // ---- SVG: per-wall panel placement (small multiples). Faithfully draws the
  // engine's actual layout — one labeled mini-elevation per wall with panels
  // WITHIN that wall's own bounds + a per-wall count + a ceiling tally. Each wall
  // is scaled to its own box, so a busy wall can never overflow the frame. Used on
  // screen AND rasterized to the render2d PNG for the proposal vision step. ----
  function wallLayoutSvg(plan) {
    var planes = (plan.planes || []).slice().sort(function (a, b) { return a.index - b.index; });
    var wallP = plan.placed.filter(function (p) { return p.mount === 'wall' && !p.preserved && p.offsetAlongWall != null; });
    var ceilingCount = plan.placed.filter(function (p) { return p.mount === 'ceiling' && !p.preserved; }).length;
    var preserved = plan.placed.filter(function (p) { return p.preserved; }).length;
    var byWall = {};
    wallP.forEach(function (p) { (byWall[p.wallId] = byWall[p.wallId] || []).push(p); });
    var NAMES = ['Front wall', 'Right wall', 'Back wall', 'Left wall'];

    var VBW = 300, pad = 6, gap = 8, cols = 2;
    var boxW = (VBW - pad * 2 - gap * (cols - 1)) / cols;
    var labelH = 13, wallBoxH = 44, cellGapY = 10, rowH = labelH + wallBoxH + cellGapY;
    var rows = Math.max(1, Math.ceil(planes.length / cols));
    var gridH = rows * rowH;
    var ceilBlockH = ceilingCount > 0 ? 46 : 0;
    var footerH = preserved > 0 ? 13 : 0;
    var VBH = Math.round(pad + gridH + ceilBlockH + footerH + pad);

    var s = '<svg viewBox="0 0 ' + VBW + ' ' + VBH + '" width="100%" height="' + VBH + '" role="img" aria-label="Panel placement per wall" xmlns="http://www.w3.org/2000/svg">';

    planes.forEach(function (plane, i) {
      var col = i % cols, row = Math.floor(i / cols);
      var bx = pad + col * (boxW + gap);
      var by = pad + row * rowH;
      var arr = byWall[plane.wallId] || [];
      var nm = NAMES[plane.index] != null ? NAMES[plane.index] : ('Wall ' + (plane.index + 1));
      s += '<text x="' + bx + '" y="' + (by + 9) + '" font-size="9" font-weight="700" fill="#232323">' + nm + '</text>';
      s += '<text x="' + (bx + boxW).toFixed(1) + '" y="' + (by + 9) + '" text-anchor="end" font-size="9" font-weight="800" fill="#ea0606">' + arr.length + '</text>';
      var wy = by + labelH;
      s += '<rect x="' + bx + '" y="' + wy + '" width="' + boxW.toFixed(1) + '" height="' + wallBoxH + '" rx="2" fill="#f6f6f6" stroke="#e4e4e4"/>';
      if (arr.length) {
        var ip = 3, innerW = boxW - ip * 2, innerH = wallBoxH - ip * 2;
        var wlM = plane.length, whM = plane.height;
        var pWm = arr[0].widthMeters, pHm = arr[0].heightMeters;
        var sx = innerW / wlM, sy = innerH / whM;
        var floorPx = wy + ip + innerH;
        arr.forEach(function (p) {
          var cxM = wlM / 2 + p.offsetAlongWall;
          var x = bx + ip + cxM * sx - (pWm * sx) / 2;
          var cyF = p.centerY != null ? p.centerY : whM / 2;
          var y = floorPx - (cyF * sy) - (pHm * sy) / 2;
          var w = Math.max(1.4, pWm * sx), h = Math.max(3, pHm * sy);
          if (x < bx + ip) x = bx + ip;
          if (x + w > bx + boxW - ip) x = bx + boxW - ip - w;
          if (y < wy + ip) y = wy + ip;
          if (y + h > wy + wallBoxH - ip) y = wy + wallBoxH - ip - h;
          s += '<rect x="' + x.toFixed(1) + '" y="' + y.toFixed(1) + '" width="' + w.toFixed(1) + '" height="' + h.toFixed(1) + '" rx="1" fill="#ea0606" opacity="0.92"/>';
        });
      } else {
        s += '<text x="' + (bx + boxW / 2).toFixed(1) + '" y="' + (wy + wallBoxH / 2 + 3) + '" text-anchor="middle" font-size="8" fill="#c4c4c4">none</text>';
      }
    });

    if (ceilingCount > 0) {
      var cyTop = pad + gridH;
      s += '<text x="' + pad + '" y="' + (cyTop + 9) + '" font-size="9" font-weight="700" fill="#232323">Ceiling</text>';
      s += '<text x="' + (VBW - pad) + '" y="' + (cyTop + 9) + '" text-anchor="end" font-size="9" font-weight="800" fill="#ea0606">' + ceilingCount + '</text>';
      var cwy = cyTop + labelH, cwH = ceilBlockH - labelH - 4, cwW = VBW - pad * 2;
      s += '<rect x="' + pad + '" y="' + cwy + '" width="' + cwW + '" height="' + cwH + '" rx="2" fill="#f6f6f6" stroke="#e4e4e4"/>';
      var showN = Math.min(ceilingCount, 30);
      var perRow = Math.max(1, Math.min(showN, 15));
      var gRows = Math.max(1, Math.ceil(showN / perRow));
      var cip = 4, cellW = (cwW - cip * 2) / perRow, cellH = (cwH - cip * 2) / gRows;
      var cw = Math.max(1.4, cellW - 1.5), ch = Math.max(2, cellH - 1.5);
      for (var k = 0; k < showN; k++) {
        var rr = Math.floor(k / perRow), cc = k % perRow;
        var cxp = pad + cip + cc * cellW, cyp = cwy + cip + rr * cellH;
        s += '<rect x="' + cxp.toFixed(1) + '" y="' + cyp.toFixed(1) + '" width="' + cw.toFixed(1) + '" height="' + ch.toFixed(1) + '" rx="1" fill="#ea0606" opacity="0.85"/>';
      }
    }

    if (preserved > 0) {
      s += '<text x="' + (VBW / 2) + '" y="' + (VBH - 4) + '" text-anchor="middle" font-size="8" fill="#9a9a9a">+' + preserved + ' more panels for a room this large</text>';
    }
    s += '</svg>';
    return s;
  }

  // viewBox -> raster pixel dims (2x) so svgToPng keeps the SVG aspect ratio.
  function rasterDims(svgStr) {
    var m = /viewBox="0 0 (\d+(?:\.\d+)?) (\d+(?:\.\d+)?)"/.exec(svgStr);
    var w = m ? parseFloat(m[1]) : 300, h = m ? parseFloat(m[2]) : 96;
    return { w: Math.round(w * 2), h: Math.round(h * 2) };
  }

  // ---- Rasterize an SVG string to a PNG Blob (render2d for proposal vision) ----
  function svgToPng(svgStr, w, h) {
    return new Promise(function (resolve) {
      try {
        var canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        var ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, w, h);
        var img = new Image();
        var svg = svgStr.indexOf('xmlns') > -1 ? svgStr : svgStr.replace('<svg ', '<svg xmlns="http://www.w3.org/2000/svg" ');
        var url = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svg)));
        img.onload = function () { ctx.drawImage(img, 0, 0, w, h); canvas.toBlob(function (b) { resolve(b); }, 'image/png'); };
        img.onerror = function () { resolve(null); };
        img.src = url;
      } catch (e) { resolve(null); }
    });
  }

  // ======================= SCREEN RENDERERS =======================
  var NAV = '<div class="pmr-nav"><div class="pmr-logo">Acoustic<span>.</span>SoundPanels</div><a class="pmr-navph" href="tel:+18889235777">' + PHONE + '</a></div>';

  function progress(active) {
    var s = '<div class="pmr-prog">';
    for (var i = 0; i < 4; i++) { s += '<i class="' + (i < active ? 'on' : '') + '"></i>'; }
    return s + '</div>';
  }

  function helpBtn() { return '<button class="pmr-qmark" data-help="1" aria-label="Help">?</button>'; }

  function screenS0() {
    // S0 fork: one planning door, two paths (Dan 2026-07-13). Card 1 = DIY (primary red)
    // advances to S1; Card 2 = expert (outlined) links to the Free Room Analysis page.
    // The old ghost FRA link was removed because Card 2 replaces it.
    // ==========================================================================
    // MIRRORED COPY — HARD RULE. The strings in this S0 fork are mirrored into the
    // homepage snippet snippets/asp-quiet-fork.liquid (rendered above the fold on
    // the ASP homepage). This fork is generated in JS so the snippet cannot render
    // it; the copy therefore lives in two places on purpose. If you change a word
    // here (eyebrow / headline / lede / card titles / card descriptions / CTA
    // labels / phone line), make the IDENTICAL change in asp-quiet-fork.liquid,
    // and vice-versa. Do not let them drift.
    // ==========================================================================
    return NAV + '<div class="pmr-scr">' +
      '<h3 style="margin-bottom:20px">Unwanted noise? Start here:</h3>' +
      '<div class="pmr-card" style="border-color:var(--red);background:#fff5f5">' +
      '<div class="pmr-fra-t">Do it myself</div>' +
      '<div class="pmr-fra-d">Your plan and price in 60 seconds.</div>' +
      '<button class="pmr-btn pri" data-go="s1">Start</button></div>' +
      '<div class="pmr-card">' +
      '<div class="pmr-fra-t">Have an expert do it</div>' +
      '<div class="pmr-fra-d">We study your room and send the exact plan.</div>' +
      '<a class="pmr-btn sec" href="' + FRA_URL + '" data-fork="expert">Free Room Analysis</a></div>' +
      '<a class="pmr-btn ghost" href="tel:+18889235777">Or call ' + PHONE + '</a>' +
      '</div>';
  }

  // quiet_fork_click tracking (planner S0). Mirrors the nav_click / homepage-fork
  // gtag pattern: fails silent without gtag, wrapped in try/catch. source:'planner'.
  function trackFork(path) {
    try { if (typeof gtag === 'function') { gtag('event', 'quiet_fork_click', { source: 'planner', path: path }); } } catch (e) {}
  }

  function screenS1() {
    var tiles = ROOM_TYPES.map(function (t) {
      var on = st.space && st.space.key === t.key ? ' on' : '';
      return '<button class="pmr-tile' + on + '" data-space="' + t.key + '"><span class="ic">' + t.ic + '</span>' + t.label + '</button>';
    }).join('');
    return NAV + '<div class="pmr-scr">' + helpBtn() + progress(1) +
      '<span class="pmr-eyebrow">Step 1 of 4</span>' +
      '<h3>What room are we quieting?</h3>' +
      '<p class="pmr-lede">Pick your space.</p>' +
      '<div class="pmr-tiles">' + tiles + '</div>' +
      helpBox('Each space type sets a starting size and how many panels ASP suggests. You can change everything on the next screen.') +
      '</div>';
  }

  function stepper(label, key, unit, hint) {
    var v = st[key];
    // The value is tap-to-type (data-dimval). Before that, +/- at 1ft a click was
    // the ONLY way to cross a 5-200ft range, so visitors hammered the buttons:
    // 257 rage-click bursts on .pmr-stepb across 154 sessions (friction analytics,
    // Jul 13 - Aug 27 2026), and a minority clicked this span expecting to type.
    return '<div class="pmr-fld"><label>' + label + '</label>' +
      '<div class="pmr-inp"><button class="pmr-stepb" data-dim="' + key + '" data-d="-1" aria-label="Decrease ' + label + '">−</button>' +
      '<span class="pmr-v" id="pmr-' + key + '" data-dimval="' + key + '" role="button" tabindex="0" title="Tap to type an exact size">' + v + ' <span class="u">' + unit + '</span></span>' +
      '<button class="pmr-stepb" data-dim="' + key + '" data-d="1" aria-label="Increase ' + label + '">+</button></div>' +
      (hint ? '<div class="pmr-ghost">' + hint + '</div>' : '') + '</div>';
  }

  function screenS2() {
    // One screen: room size AND optional photos. Either input alone is enough to
    // proceed (dims carry rough defaults from the space type; photos are optional).
    // st.photos + st.L/W/H both feed the payload independently, so no mode branch.
    var labels = ['Wall 1', 'Wall 2', 'Wall 3', 'Wall 4', 'Ceiling'];
    var phTiles = labels.map(function (l) {
      var has = !!st.photos[l];
      return '<label class="pmr-tile phototile' + (has ? ' on' : '') + '"><span class="ic">' + (has ? '✓' : (l === 'Ceiling' ? '🗺' : '📷')) + '</span>' + l +
        '<input type="file" accept="image/*" data-photo="' + l + '" style="display:none"></label>';
    }).join('');
    // Problem chips (Phase 2, W1): optional; picking one only ever UPGRADES the
    // suggested tier. Tap again to clear.
    var prChips = PROBLEMS.map(function (pr) {
      var on = st.problem === pr.key ? ' on' : '';
      return '<button class="pmr-chip' + on + '" data-problem="' + pr.key + '">' + pr.label + '</button>';
    }).join('');
    var body =
      stepper('Length', 'L', 'ft', 'A rough guess is fine. We dial it in on your proposal.') +
      stepper('Width', 'W', 'ft', '') +
      stepper('Ceiling height', 'H', 'ft', '') +
      '<div class="pmr-fld" style="margin-top:6px"><label>What’s the problem? (optional)</label>' +
      '<div class="pmr-chips" style="margin-top:6px">' + prChips + '</div></div>' +
      '<div class="pmr-fld" style="margin-top:6px"><label>Add photos (optional). Sharper plan, exact quote.</label>' +
      '<div class="pmr-tiles ph3" style="margin-top:6px">' + phTiles + '</div></div>' +
      (APP_STORE_URL ? '<p class="pmr-ghost" style="margin:2px 0 12px">iPhone? <a href="' + APP_STORE_URL + '" style="color:inherit;text-decoration:underline">Our app measures your room by camera.</a></p>' : '') +
      '<button class="pmr-btn pri" data-go="surfaces">See my plan</button>';
    return NAV + '<div class="pmr-scr">' + helpBtn() + progress(1) +
      '<h3 style="font-size:19px">Tell us about the room</h3>' +
      '<p class="pmr-lede">Room size, photos, or both. Rough is fine.</p>' +
      body +
      helpBox('Rough is fine. The room size gives you a starting plan, and photos make the layout sharper. A specialist confirms the exact layout and full price on your proposal.') + '</div>';
  }

  function miniDim(label, key) {
    return '<div class="pmr-mini"><span class="ml">' + label + '</span>' +
      '<button class="pmr-stepb" data-dim="' + key + '" data-d="-1">−</button>' +
      '<b id="pmr-mini-' + key + '">' + st[key] + '</b>' +
      '<button class="pmr-stepb" data-dim="' + key + '" data-d="1">+</button></div>';
  }

  // ======================= PHASE 2: SURFACE PICKER (W2) =======================
  var WALL_SHORT = ['Front', 'Right', 'Back', 'Left']; // by plane index (w0..w3)

  // Top-down room: floor to the entered aspect ratio, four tappable wall bars.
  // Open = solid teal, skip = red dashed. Fat invisible hit rects carry the tap
  // targets so a thumb never has to land on a 12px bar.
  function surfTopSvg() {
    var VBW = 300, VBH = 216;
    var scale = Math.min(180 / st.L, 108 / st.W);
    var fw = Math.max(70, st.L * scale), fh = Math.max(56, st.W * scale);
    var fx = (VBW - fw) / 2, fy = (VBH - fh) / 2 + 4;
    var T = 12, G = 5, HIT = 34;
    function bar(id, x, y, w, h) {
      var skip = !!st.skipWalls[id];
      var vis = skip
        ? '<rect x="' + x + '" y="' + y + '" width="' + w + '" height="' + h + '" rx="2.5" fill="#fff" stroke="#ea0606" stroke-width="1.6" stroke-dasharray="5 3"/>'
        : '<rect x="' + x + '" y="' + y + '" width="' + w + '" height="' + h + '" rx="2.5" fill="#108474"/>';
      return vis;
    }
    function hit(id, x, y, w, h) {
      return '<rect data-wall="' + id + '" x="' + x + '" y="' + y + '" width="' + w + '" height="' + h + '" fill="#000" fill-opacity="0" style="cursor:pointer"/>';
    }
    function lbl(id, i, x, y, rot) {
      var skip = !!st.skipWalls[id];
      var txt = WALL_SHORT[i] + ' · ' + (skip ? 'skip' : 'panels OK');
      var fill = skip ? '#ea0606' : '#108474';
      var tr = rot ? ' transform="rotate(' + rot + ' ' + x + ' ' + y + ')"' : '';
      return '<text x="' + x + '" y="' + y + '" font-size="10" font-weight="700" fill="' + fill + '" text-anchor="middle"' + tr + '>' + txt + '</text>';
    }
    var s = '<svg viewBox="0 0 ' + VBW + ' ' + VBH + '" width="100%" role="img" aria-label="Top-down room, tap a wall to skip it" xmlns="http://www.w3.org/2000/svg">';
    s += '<rect x="' + fx + '" y="' + fy + '" width="' + fw + '" height="' + fh + '" fill="#fafafa" stroke="#e4e4e4"/>';
    s += '<text x="' + (VBW / 2) + '" y="' + (fy + fh / 2 - 2) + '" font-size="11" fill="#9a9a9a" text-anchor="middle">' + st.L + ' x ' + st.W + ' ft</text>';
    s += '<text x="' + (VBW / 2) + '" y="' + (fy + fh / 2 + 12) + '" font-size="9" fill="#c0c0c0" text-anchor="middle">tap a wall to toggle</text>';
    // bars: w0 top (front), w1 right, w2 bottom (back), w3 left
    s += bar('w0', fx, fy - G - T, fw, T) + bar('w2', fx, fy + fh + G, fw, T);
    s += bar('w1', fx + fw + G, fy, T, fh) + bar('w3', fx - G - T, fy, T, fh);
    s += lbl('w0', 0, VBW / 2, fy - G - T - 6, 0);
    s += lbl('w2', 2, VBW / 2, fy + fh + G + T + 13, 0);
    s += lbl('w1', 1, fx + fw + G + T + 9, fy + fh / 2, 90);
    s += lbl('w3', 3, fx - G - T - 9, fy + fh / 2, -90);
    // fat hit targets on top
    s += hit('w0', fx - HIT / 2, fy - G - HIT, fw + HIT, HIT) + hit('w2', fx - HIT / 2, fy + fh + G, fw + HIT, HIT);
    s += hit('w1', fx + fw + G, fy - HIT / 2, HIT, fh + HIT) + hit('w3', fx - G - HIT, fy - HIT / 2, HIT, fh + HIT);
    s += '</svg>';
    return s;
  }

  function screenSurf() {
    var p = computePlan();
    var openWalls = (p.wallInfo || []).filter(function (w) { return !w.skipped; }).length;
    var withCeil = st.ceilingOn && p.ceilingCount > 0;
    var strip = '<div class="pmr-price"><div style="display:flex;justify-content:space-between;align-items:baseline;width:100%">' +
      '<span class="pl">Plan updates live</span>' +
      '<span class="pv" style="font-size:16px">' + p.count + ' panels · ' + openWalls + (openWalls === 1 ? ' wall' : ' walls') + (withCeil ? ' + ceiling' : '') + '</span></div></div>';
    // Warn ONLY when the CUSTOMER constrained the surfaces (skips / ceiling
    // off). The untouched flow can also carry engine overflow (single-row band
    // preview fits fewer than the recommendation in tall-count rooms) — that is
    // Phase-1 behavior and not the customer's doing; never blame them for it.
    var constrained = skippedWallIds().length > 0 || !st.ceilingOn;
    var over = (p.overflow && constrained)
      ? '<div class="pmr-note">Your room needs more surface than you’ve allowed. The plan keeps what fits; send it and a specialist covers the rest.</div>'
      : '';
    var ceilCard = '<div class="pmr-card" style="margin-top:2px"><div style="display:flex;justify-content:space-between;align-items:center">' +
      '<span style="font-size:13px;font-weight:700">Treat the ceiling too</span>' +
      '<button class="pmr-switch' + (st.ceilingOn ? ' on' : '') + '" data-ceil="1" role="switch" aria-checked="' + (st.ceilingOn ? 'true' : 'false') + '" aria-label="Treat the ceiling too"><i></i></button>' +
      '</div></div>';
    return NAV + '<div class="pmr-scr">' + helpBtn() + progress(2) +
      '<span class="pmr-eyebrow">Step 2 of 4</span>' +
      '<h3>Where can panels go?</h3>' +
      '<p class="pmr-lede">Tap a wall to skip it (windows, brick, doors). Your plan adapts. All open? Keep going.</p>' +
      '<div class="pmr-card" style="padding:8px 6px 4px">' + surfTopSvg() + '</div>' +
      ceilCard + strip + over +
      '<button class="pmr-btn pri" data-go="plan">Preview my layout</button>' +
      helpBox('Skip any wall that cannot hold panels: windows, brick, doors, shelving. The same coverage spreads across the walls and ceiling you leave open. Nothing here changes how quiet the room gets, only where the panels go.') +
      '</div>';
  }

  // ---- Fabric line selector + named color swatches (the S3 "Fabric & Color" card).
  // Fabric type is explicit; every color shows its real name; the pick is echoed. ----
  function fabricColorCard(p) {
    var tiles = availableTiles(24, 48, 2);
    if (!tiles.length) return '';
    var selKey = st.tileKey;
    var lineHtml = tiles.map(function (o) {
      var on = o.tile.key === selKey ? ' on' : '';
      var from = o.fromCents != null ? '<span class="pmr-fl-from">from ' + money(o.fromCents) + '</span>' : '';
      return '<button class="pmr-fl' + on + '" data-fabric="' + o.tile.key + '"><span class="pmr-fl-name">' + o.tile.label + '</span>' + from + '</button>';
    }).join('');

    var colors = p.colors || [];
    var swHtml = colors.map(function (c, i) {
      var on = (p.color && c.name === p.color.name && c.line === p.color.line) ? ' on' : '';
      var face;
      if (c.image) {
        face = '<span class="pmr-sw2-face"><img src="' + c.image + '" loading="lazy" decoding="async" alt=""></span>';
      } else {
        var hx = colorChipHex(c.name); // real representative chip, never a blank gray square
        face = hx ? '<span class="pmr-sw2-face" style="background:' + hx + '"></span>'
                  : '<span class="pmr-sw2-face neutral"></span>';
      }
      return '<button class="pmr-sw2' + on + '" data-coloridx="' + i + '" title="' + c.name + '">' + face + '<span class="pmr-sw2-name">' + c.name + '</span></button>';
    }).join('');

    var selName = p.color ? p.color.name : '';
    var echo = '<div class="pmr-fabecho"><b>' + (p.fabricLabel || '') + '</b>' + (selName ? ': ' + selName : '') + '</div>';
    return '<div class="pmr-card"><div class="ct">Fabric &amp; Color</div>' +
      '<div class="pmr-fabrics">' + lineHtml + '</div>' + echo +
      '<div class="pmr-swatches2">' + swHtml + '</div></div>';
  }

  // Forward-ready commercial signal. The planner does not yet capture a company name; when a
  // company field lands, wire it here. Until then this is false, so the residential smallMax applies.
  function isCommercial() { return !!(st.company && String(st.company).trim()); }

  // Slice 2.3: the large-format (48x96) buy path is offered only when ALL hold:
  //   - the >300 sqft large-panel rule fired,
  //   - the 48x96 plan resolved to a live, purchasable variant, and
  //   - the plan clears the theme's 5-piece large-format freight minimum.
  // Otherwise the flow falls back to the pre-2.3 behavior (standard display +
  // FRA-primary routing), never a large cart that would error at checkout. FRA
  // stays the RECOMMENDED exit for these rooms; the large cart is the active
  // alternative (the revenue unlock: it used to sell standard panels or dead-end).
  function largeBuyActive(p) {
    return !!(p && p.isLarge && p.large && p.large.isPurchasable &&
      p.large.variant && p.large.variant.id && p.large.count >= LARGE_FREIGHT_MIN);
  }

  // Decide the RECOMMENDED primary exit for the current plan: 'cart' | 'send' | 'fra'.
  // Physical rules run first and override the dollar bands (see ROUTING provenance notes).
  function classifyExit(p) {
    if (!p) return 'send';
    var coverageSqFt = (p.count || 0) * (p.panelFaceSqFt || 0);
    var longDimIn = Math.max(p.pw || 0, p.ph || 0);
    var freight = (p.isLarge === true) || (longDimIn > ROUTING.freightLongDimIn);
    var dollars = (p.pricePoint || 0) / 100;
    // 1) PHYSICAL RULES FIRST (freight / scale) — override dollars.
    if (freight || coverageSqFt >= ROUTING.largeSqft) return 'fra';
    if (coverageSqFt >= ROUTING.midSqftFloor) return 'send';
    // 2) DOLLAR FALLBACK — only when the physical signals do not decide.
    var smallCeil = isCommercial() ? ROUTING.smallMaxCommercial : ROUTING.smallMax;
    if (dollars < smallCeil) return 'cart';
    if (dollars <= ROUTING.midMax) return 'send';
    return 'fra';
  }

  // Render all three exits, re-ranked, with ONE "Recommended" badge on the primary.
  // The cart is NEVER hidden. FRA is a bottom exit only when it is the primary (large/complex);
  // on small/mid FRA stays represented by the standalone S3 FRA card above (no double FRA block).
  function exitsBlock(primary, p, blocked) {
    // Phase 2 cart guard: when the theme's cart minimums (or an empty plan)
    // would reject this cart at checkout, the cart CTA is replaced with the
    // reason + the fix. Steering beats a checkout error.
    var cartNote = '';
    if (blocked === 'guilford') {
      cartNote = '<div class="pmr-note" style="margin:10px 0 0">Guilford of Maine has a 6 panel minimum and your plan has ' + p.count + '. Add panels, pick Burch Prime Time (no minimum), or send your plan for exact options.</div>';
    } else if (blocked === 'empty') {
      cartNote = '<div class="pmr-note" style="margin:10px 0 0">No panels in this plan yet. Open a wall or add panels to buy online, or send your plan.</div>';
    }
    var cart = function (cls, label) {
      if (blocked) return cartNote;
      return '<button class="pmr-btn ' + cls + '" data-go="cart">' + label + '</button>';
    };
    var send = function (cls, label) { return '<button class="pmr-btn ' + cls + '" data-go="s4">' + label + '</button>'; };
    var fra  = function (cls, label) { return '<a class="pmr-btn ' + cls + '" href="' + FRA_URL + '">' + label + '</a>'; };
    var badge = '<span class="pmr-badge">Recommended</span>';
    var out = '<div class="pmr-exits">';
    if (primary === 'cart') {
      out += '<div class="pmr-exit">' + badge + cart('pri', 'Add these panels to your cart') + '</div>';
      out += send('sec', 'Send My Plan');
    } else if (primary === 'fra') {
      out += '<div class="pmr-exit">' + badge + fra('pri', 'Free Room Analysis') + '</div>';
      out += '<p class="pmr-center-fine" style="margin:6px 0 0">A room this size deserves expert eyes. We plan it for you, free.</p>';
      out += send('sec', 'Send My Plan');
      // Slice 2.3: for a buyable large room the cart is an ACTIVE alternative that
      // sells the real 48x96 plan (not a ghost link to standard panels). FRA keeps
      // the Recommended badge; the freight terms are shown right on the CTA.
      if (largeBuyActive(p)) {
        out += cart('sec', 'Buy the large-format plan (' + p.large.count + ' panels, ' + money(p.large.pricePoint) + ')');
        out += '<p class="pmr-center-fine" style="margin:6px 0 0">' + FREIGHT_NOTE + '</p>';
      } else {
        out += cart('ghost', 'or add these panels to your cart');
      }
    } else { // 'send' (mid)
      out += '<div class="pmr-exit">' + badge + send('pri', 'Send My Plan') + '</div>';
      out += cart('sec', 'Add to Cart');
    }
    out += '</div>';
    return out;
  }

  // ======================= PHASE 2: INTERACTIVE LAYOUT (W3) =======================
  var NAMES_FULL = ['Front wall', 'Right wall', 'Back wall', 'Left wall'];

  // Single-wall elevation, drawn from the ENGINE's actual placement for that
  // wall (offsetAlongWall + centerY), with a dashed ghost "+1" showing that the
  // band has room for one more (band re-gaps on add, so the ghost is an
  // affordance, not an exact final position).
  function wallElevationSvg(p, wallId) {
    var plane = null;
    for (var i = 0; i < p.planes.length; i++) { if (p.planes[i].wallId === wallId) { plane = p.planes[i]; break; } }
    if (!plane) return '';
    var info = null;
    for (var j = 0; j < p.wallInfo.length; j++) { if (p.wallInfo[j].id === wallId) { info = p.wallInfo[j]; break; } }
    var arr = p.placed.filter(function (q) { return q.mount === 'wall' && !q.preserved && q.wallId === wallId; });
    var VBW = 280, VBH = 158;
    var boxW = 256, boxH = 118;
    var sx = boxW / plane.length, sy = boxH / plane.height;
    var sc = Math.min(sx, sy);
    var bw = plane.length * sc, bh = plane.height * sc;
    var bx = (VBW - bw) / 2, by = 10;
    var floorPx = by + bh;
    var s = '<svg viewBox="0 0 ' + VBW + ' ' + VBH + '" width="100%" role="img" aria-label="Wall elevation with panel overlays" xmlns="http://www.w3.org/2000/svg">';
    s += '<rect x="' + bx.toFixed(1) + '" y="' + by + '" width="' + bw.toFixed(1) + '" height="' + bh.toFixed(1) + '" fill="#fafafa" stroke="#232323" stroke-width="1.2"/>';
    // ear-height guide (46 in listening height)
    var earPx = floorPx - (1.1684 * sc);
    if (earPx > by + 8) {
      s += '<line x1="' + bx.toFixed(1) + '" y1="' + earPx.toFixed(1) + '" x2="' + (bx + bw).toFixed(1) + '" y2="' + earPx.toFixed(1) + '" stroke="#e4e4e4" stroke-dasharray="3 3"/>';
      s += '<text x="' + (bx + bw - 2).toFixed(1) + '" y="' + (earPx - 3).toFixed(1) + '" font-size="7.5" fill="#c0c0c0" text-anchor="end">ear height</text>';
    }
    var pw = 0, ph = 0, lastRight = null, gapM = null;
    arr.forEach(function (q, k) {
      pw = q.widthMeters * sc; ph = q.heightMeters * sc;
      var cxM = plane.length / 2 + q.offsetAlongWall;
      var x = bx + cxM * sc - pw / 2;
      var cyF = q.centerY != null ? q.centerY : plane.height / 2;
      var y = floorPx - (cyF * sc) - ph / 2;
      s += '<rect x="' + x.toFixed(1) + '" y="' + y.toFixed(1) + '" width="' + pw.toFixed(1) + '" height="' + ph.toFixed(1) + '" rx="1.5" fill="#108474" fill-opacity=".88" stroke="#0b5c51" stroke-width=".8"/>';
      if (k === 0 && arr.length > 1) gapM = (arr[1].offsetAlongWall - q.offsetAlongWall) - q.widthMeters;
      if (k === arr.length - 1) lastRight = { x: x + pw, y: y };
    });
    // ghost +1 affordance (band has capacity for one more)
    if (info && !info.skipped && info.count < info.cap) {
      var gw = pw || (0.6096 * sc), gh = ph || (1.2192 * sc);
      var gx, gy;
      if (lastRight) {
        var gpx = (gapM != null && gapM > 0 ? gapM : 0.1524) * sc;
        gx = Math.min(lastRight.x + gpx, bx + bw - gw - 2);
        gy = lastRight.y;
      } else {
        gx = bx + bw / 2 - gw / 2;
        var desired = floorPx - (1.1684 * sc) - gh / 2;
        gy = Math.max(by + 2, Math.min(desired, floorPx - gh - 2));
      }
      s += '<rect x="' + gx.toFixed(1) + '" y="' + gy.toFixed(1) + '" width="' + gw.toFixed(1) + '" height="' + gh.toFixed(1) + '" rx="1.5" fill="#108474" fill-opacity=".28" stroke="#0b5c51" stroke-width=".8" stroke-dasharray="3 2"/>';
      s += '<text x="' + (gx + gw / 2).toFixed(1) + '" y="' + (gy + gh / 2 + 2.5).toFixed(1) + '" font-size="7.5" fill="#0b5c51" text-anchor="middle">+1?</text>';
    }
    var ftL = Math.round(plane.length * 3.28084 * 10) / 10, ftH = Math.round(plane.height * 3.28084 * 10) / 10;
    s += '<text x="' + (VBW / 2) + '" y="' + (VBH - 6) + '" font-size="9" fill="#9a9a9a" text-anchor="middle">' + (NAMES_FULL[plane.index] || 'Wall') + ': ' + ftL + ' ft wide x ' + ftH + ' ft high</text>';
    s += '</svg>';
    return s;
  }

  // Ceiling: synthetic top-down grid (the engine places a centered grid; it does
  // not emit per-panel ceiling coordinates, so this mirrors the count honestly).
  function ceilingGridSvg(p) {
    var VBW = 280, VBH = 150;
    var scale = Math.min(240 / st.L, 108 / st.W);
    var fw = Math.max(80, st.L * scale), fh = Math.max(60, st.W * scale);
    var fx = (VBW - fw) / 2, fy = 8;
    var n = p.ceilingCount;
    var s = '<svg viewBox="0 0 ' + VBW + ' ' + VBH + '" width="100%" role="img" aria-label="Ceiling panel grid" xmlns="http://www.w3.org/2000/svg">';
    s += '<rect x="' + fx.toFixed(1) + '" y="' + fy + '" width="' + fw.toFixed(1) + '" height="' + fh.toFixed(1) + '" fill="#fafafa" stroke="#232323" stroke-width="1.2"/>';
    if (n > 0) {
      var perRow = Math.max(1, Math.min(n, Math.round(Math.sqrt(n * (fw / fh))) || 1));
      var rows = Math.ceil(n / perRow);
      var ip = 6, cellW = (fw - ip * 2) / perRow, cellH = (fh - ip * 2) / rows;
      var cw = Math.max(2, cellW - 3), chh = Math.max(2, cellH - 3);
      for (var k = 0; k < n; k++) {
        var rr = Math.floor(k / perRow), cc = k % perRow;
        s += '<rect x="' + (fx + ip + cc * cellW + 1.5).toFixed(1) + '" y="' + (fy + ip + rr * cellH + 1.5).toFixed(1) + '" width="' + cw.toFixed(1) + '" height="' + chh.toFixed(1) + '" rx="1.5" fill="#108474" fill-opacity=".88"/>';
      }
    } else {
      s += '<text x="' + (VBW / 2) + '" y="' + (fy + fh / 2 + 3).toFixed(1) + '" font-size="9" fill="#c4c4c4" text-anchor="middle">no ceiling panels</text>';
    }
    s += '<text x="' + (VBW / 2) + '" y="' + (VBH - 6) + '" font-size="9" fill="#9a9a9a" text-anchor="middle">Ceiling: ' + st.L + ' x ' + st.W + ' ft · ' + n + (n === 1 ? ' panel' : ' panels') + '</text>';
    s += '</svg>';
    return s;
  }

  // Which layout tab is active ('w0'..'w3' | 'ceiling').
  function activeLayoutTab(p) {
    var openIds = p.wallInfo.filter(function (w) { return !w.skipped; }).map(function (w) { return w.id; });
    var valid = openIds.slice();
    if (st.ceilingOn) valid.push('ceiling');
    if (st.selWall && valid.indexOf(st.selWall) >= 0) return st.selWall;
    return valid.length ? valid[0] : null;
  }

  // The interactive "Your panel layout" card: wall tabs + engine-true elevation
  // + per-surface adjust with capacity bounds and the coverage-floor guard.
  function layoutCardInteractive(p) {
    var tab = activeLayoutTab(p);
    if (!tab) {
      return '<div class="pmr-card"><div class="ct">Where the panels go</div>' +
        '<div class="pmr-note">Every wall is skipped and the ceiling is off. Open a surface to see your layout.</div>' +
        '<button class="pmr-btn sec" data-go="surfaces">Choose surfaces</button></div>';
    }
    var tabs = '';
    p.wallInfo.forEach(function (w) {
      if (w.skipped) return;
      tabs += '<button class="pmr-wtab' + (tab === w.id ? ' on' : '') + '" data-wtab="' + w.id + '">' + WALL_SHORT[w.index] + ' <span class="n">' + w.count + '</span></button>';
    });
    if (st.ceilingOn) {
      tabs += '<button class="pmr-wtab' + (tab === 'ceiling' ? ' on' : '') + '" data-wtab="ceiling">Ceiling <span class="n">' + p.ceilingCount + '</span></button>';
    }
    var svg, adjLabel, cur, cap;
    if (tab === 'ceiling') {
      svg = ceilingGridSvg(p);
      adjLabel = 'Panels on the ceiling';
      cur = p.ceilingCount; cap = p.ceilingCap;
    } else {
      svg = wallElevationSvg(p, tab);
      adjLabel = 'Panels on this wall';
      var wi = null;
      for (var i = 0; i < p.wallInfo.length; i++) { if (p.wallInfo[i].id === tab) { wi = p.wallInfo[i]; break; } }
      cur = wi ? wi.count : 0; cap = wi ? wi.cap : 0;
    }
    var minusDis = cur <= 0 ? ' disabled' : '';
    var plusDis = cur >= cap ? ' disabled' : '';
    var adj = '<div class="pmr-adjrow"><span class="al">' + adjLabel + '</span>' +
      '<span class="ctl"><button class="pmr-stepb" data-adj="' + tab + '" data-d="-1"' + minusDis + '>−</button>' +
      '<b>' + cur + '</b>' +
      '<button class="pmr-stepb" data-adj="' + tab + '" data-d="1"' + plusDis + '>+</button></span></div>';
    var guard;
    if (p.floorCount > 0 && p.count < p.floorCount) {
      guard = '<div class="pmr-note" style="margin:8px 0 0">' + p.count + ' total is below the ' + p.floorCount + ' this room needs for your coverage target. Expect a weaker result, or send your plan and a specialist balances it.</div>';
    } else {
      guard = '<p class="pmr-fine">' + p.count + ' total keeps you at your coverage target.' + (p.floorCount > 0 ? ' Dropping below ' + p.floorCount + ' weakens the result.' : '') + '</p>';
    }
    // Adjusted mode: overflow means the customer's constraints genuinely cannot
    // hold the plan — say so. Default mode: engine overflow is the Phase-1
    // single-row-preview limitation (count/price still use the recommendation);
    // keep the neutral Phase-1 note, never blame the customer.
    var over = '';
    if (p.overflow && p.adjusted) {
      over = '<div class="pmr-note" style="margin:8px 0 0">Your open surfaces hold ' + p.count + ' of the ' + p.recommendedCount + ' recommended panels. Send your plan and a specialist covers the gap (larger panels, smarter placement).</div>';
    } else if (p.overflow) {
      var extra = p.recommendedCount - (p.wallInfo || []).reduce(function (s, w) { return s + w.count; }, 0) - p.ceilingCount;
      if (extra > 0) over = '<p class="pmr-fine">+' + extra + ' more panels for a room this large (beyond this single-row preview). Your count and price include all ' + p.count + '.</p>';
    }
    var resetBtn = p.adjusted
      ? '<button class="pmr-btn ghost" style="margin-top:4px" data-resetlayout="1">Reset to the recommended layout</button>'
      : '';
    var editWalls = '<button class="pmr-btn ghost" style="margin-top:0" data-go="surfaces">Edit which walls can hold panels</button>';
    return '<div class="pmr-card"><div class="ct">Your panel layout</div>' +
      '<div class="pmr-wtabs">' + tabs + '</div>' +
      '<div class="pmr-wallviz">' + svg + '</div>' +
      adj + guard + over +
      '<div class="pmr-legend" style="margin-top:8px"><span><i style="background:#108474"></i>Each block is one 24 x 48 x ' + st.thickness + ' in panel</span></div>' +
      resetBtn + editWalls + '</div>';
  }

  // Cart guard (money path): never build a cart the theme's minimum-quantity
  // rules will reject at checkout. Guilford of Maine pools require 6+
  // (variant_title 'Guilford' OR line property 'Guilford Fabric Color' — this
  // planner's Guilford picks carry that property). Burch has no minimum.
  function cartBlockReason(p) {
    if (!p || p.count < 1) return 'empty';
    // Slice 2.3: the large-format cart uses discrete per-color 48x96 variants and
    // already clears the freight minimum via largeBuyActive(), so the 24" Guilford
    // 6-panel pool guard does not apply to it.
    if (largeBuyActive(p)) return null;
    if (st.tileKey === 'guilford' && p.count < 6) return 'guilford';
    return null;
  }

  function screenS3() {
    var p = computePlan();
    var tierOutcome = { Comfort: 'to take the edge off', Reference: 'for quiet, natural sound', Studio: 'for dead quiet' };
    var primaryExit = classifyExit(p); // 'cart' | 'send' | 'fra' — re-ranks the exits below
    // Phase 2 guards: a plan below the tier's coverage floor, or one the theme
    // cart minimums would reject, never carries the Recommended badge on Cart.
    var blocked = cartBlockReason(p);
    if (primaryExit === 'cart' && (blocked || (p.floorCount > 0 && p.count < p.floorCount))) primaryExit = 'send';
    // Panel size the plan uses. Displayed plan is always the standard 24x48 face at
    // the recommended thickness; large rooms get a large-format 48x96 note (Fix 3).
    var faceSize = '24 x 48 x ' + st.thickness + ' in';
    var largeSize = '48 x 96 x ' + st.thickness + ' in';
    var stats = '<div class="pmr-bignum">' +
      '<div class="pmr-stat"><div class="big red">' + p.count + '</div><div class="lab">Panels</div></div>' +
      '<div class="pmr-stat"><div class="big">' + p.coveragePct + '%</div><div class="lab">Treated</div></div>' +
      '<div class="pmr-stat"><div class="big" style="font-size:15px;letter-spacing:-.02em">24×48×' + st.thickness + '</div><div class="lab">Panel size (in)</div></div></div>';
    var sizeLine = '<p class="pmr-planline">Your plan uses <b>' + p.count + ' panels, ' + faceSize + '</b>.</p>';
    var price = '<div class="pmr-price"><div><div class="pl">Estimated range</div>' +
      '<div class="pv">' + money(p.priceLow) + ' – ' + money(p.priceHigh) + '</div>' +
      '<div class="modeled">Real prices. Your exact total comes with your plan.</div></div></div>';
    var largeNote = p.isLarge ? '<div class="pmr-note">Large space. We use large-format panels (' + largeSize + '). Easier install, less labor cost.</div>' : '';
    // Size flexibility: same coverage covered by fewer, larger panels. Counts computed
    // live from the plan (standard 24x48 face = 8 sqft; large-format 48x96 = 32 sqft;
    // equivalent = ceil(coverage / 32)). Savings are LABOR/install (fewer holes, less
    // hanging time) — NOT a panel-price claim: material $/sqft is similar or higher for
    // large-format, so no price comparison is shown (large-format unit pricing is not
    // cleanly derivable client-side). Follow-up: a "show me the large-panel version"
    // toggle that re-renders stats/layout and sets sizePreference:'large' in the payload.
    var LARGE_FACE_SQFT = 32;
    var stdFaceSqFt = p.panelFaceSqFt || 8;
    var coverageSqFt = p.count * stdFaceSqFt;
    var largeCount = Math.ceil(coverageSqFt / LARGE_FACE_SQFT);
    var sizeFlexBlock = '';
    if (largeCount >= 1 && largeCount < p.count) {
      var lead = p.isLarge
        ? 'Large space. We use large-format panels (' + largeSize + ') here. '
        : '';
      sizeFlexBlock = '<div class="pmr-card"><div class="ct">Fewer, bigger panels</div>' +
        '<p class="pmr-planline" style="margin:0">' + lead +
        'Same coverage, fewer panels: <b>' + largeCount + ' large-format panels (' + largeSize + ')</b> cover what these <b>' + p.count + '</b> do. ' +
        'Fewer panels means less hanging time and fewer holes in your wall. We build any size.</p></div>';
    }
    // Slice 2.3: when the large-format plan is actually buyable (live 48x96 variant
    // that clears the freight minimum), the descriptive note becomes a REAL priced
    // card with the freight terms, so a large-room DIY buyer can purchase in-flow.
    if (largeBuyActive(p)) {
      var lg = p.large;
      sizeFlexBlock = '<div class="pmr-card"><div class="ct">Buy the large-format plan</div>' +
        '<p class="pmr-planline" style="margin:0 0 10px">A room this size treats best with fewer, bigger panels. ' +
        '<b>' + lg.count + ' large-format panels (' + lg.sizeLabel + ')</b> cover what ' + p.count + ' standard panels do, with fewer holes in your wall and less hanging time.</p>' +
        '<div class="pmr-price" style="margin:0 0 10px"><div><div class="pl">Large-format plan</div>' +
        '<div class="pv">' + money(lg.pricePoint) + '</div>' +
        '<div class="modeled">' + lg.count + ' panels at ' + money(lg.unitCents) + ' each. Real catalog price.</div></div></div>' +
        '<p class="pmr-fine" style="margin:0">' + FREIGHT_NOTE + '</p></div>';
    }
    // Single authoritative size message: rich flex block when large-format reduces the
    // count, otherwise the plain large-space note on large rooms. Never both.
    var sizeMsg = sizeFlexBlock || (p.isLarge ? largeNote : '');
    var rt60 = '<div class="pmr-card"><div class="ct">Echo, before and after</div>' + rt60Svg(p.rep) +
      '<div class="pmr-legend"><span><i style="background:#cfcfcf"></i>Before</span><span><i style="background:#ea0606"></i>After, modeled</span></div>' +
      '<p class="pmr-fine">Panels kill the ringing in voices and everyday noise. Deep bass takes bass traps.</p></div>';
    // Phase 2: the static small-multiples card became the interactive layout
    // card (tabs + engine-true elevation + per-surface adjust). wallLayoutSvg
    // still renders the render2d PNG for the proposal pipeline untouched.
    var layout = layoutCardInteractive(p) +
      (p.isLarge ? '<p class="pmr-fine" style="margin:-8px 0 14px">For a room this size we use large-format ' + largeSize + ' panels. Easier install, less labor.</p>' : '');
    // FRA: the expert-does-it-for-you alternative to Send My Plan. The planner feeds it.
    // On the large/complex profile FRA becomes the badged PRIMARY exit below, so this standalone
    // card is suppressed to avoid two competing FRA blocks (coordinator 2026-07-13). Kept on small/mid.
    var fraCard = (primaryExit === 'fra') ? '' : ('<div class="pmr-card pmr-fra">' +
      '<div class="pmr-fra-t">Want this dialed in by a pro?</div>' +
      '<div class="pmr-fra-d">Get a Free Room Analysis. An ASP expert studies your room and sends the exact plan, at no cost.</div>' +
      '<a class="pmr-btn sec pmr-fra-btn" href="' + FRA_URL + '">Get a Free Room Analysis</a></div>');
    var tierLabel = { Comfort: 'Good', Reference: 'Better', Studio: 'Best' };
    var tierMeaning = { Comfort: 'Takes the edge off', Reference: 'Quiet, natural sound', Studio: 'Dead quiet' };
    var tiers = ['Comfort', 'Reference', 'Studio'].map(function (t) {
      return '<button class="' + (st.tier === t ? 'on' : '') + '" data-tier="' + t + '">' + tierLabel[t] + '</button>';
    }).join('');
    var tierCard = '<div class="pmr-card"><div class="ct">How quiet?</div><div class="pmr-tierpick">' + tiers + '</div>' +
      '<p class="pmr-fine" style="margin-top:10px">' + (tierMeaning[st.tier] || '') + '</p></div>';
    var colorCard = fabricColorCard(p);
    return NAV + '<div class="pmr-scr">' + helpBtn() + progress(3) +
      '<span class="pmr-eyebrow">Step 3 of 4 • your plan</span>' +
      '<h3 style="margin-bottom:8px">Your room needs ' + p.count + ' panels ' + (tierOutcome[st.tier] || 'for quiet, natural sound') + '.</h3>' +
      sizeLine + stats + sizeMsg + price + rt60 + layout + fraCard + tierCard + colorCard +
      exitsBlock(primaryExit, p, blocked) +
      '<button class="pmr-btn ghost" data-go="emailcopy">Email me a copy</button>' +
      // Slice 3.1: hand the plan to whoever approves the spend. Hidden entirely
      // if the loaded engine bundle predates the codec, so the button can never
      // render dead.
      (shareReady() ? '<button class="pmr-btn ghost" data-go="share">Share this plan</button>' : '') +
      helpBox('This sets how quiet the room gets, from taking the edge off to dead quiet. Full pricing comes with your plan.') +
      '</div>';
  }

  function screenS4() {
    return NAV + '<div class="pmr-scr">' + progress(4) +
      '<span class="pmr-eyebrow">Step 4 of 4</span>' +
      '<h3>Where do we send your plan?</h3>' +
      '<p class="pmr-lede">A specialist builds your full plan with final pricing and follows up. No pressure.</p>' +
      '<form id="pmr-form" autocomplete="on">' +
      '<div class="pmr-formrow">' +
      '<div class="pmr-fld"><label>First name <span class="req">*</span></label><input class="pmr-tinp" name="firstName" required maxlength="80" placeholder="First name"></div>' +
      '<div class="pmr-fld"><label>Zip</label><input class="pmr-tinp" name="zip" inputmode="numeric" maxlength="10" placeholder="Zip"></div></div>' +
      '<div class="pmr-fld"><label>Email <span class="req">*</span></label><input class="pmr-tinp" name="email" type="email" required maxlength="200" placeholder="you@email.com"></div>' +
      '<div class="pmr-formrow">' +
      '<div class="pmr-fld"><label>Phone</label><input class="pmr-tinp" name="phone" type="tel" maxlength="40" placeholder="(optional)"></div>' +
      '<div class="pmr-fld"><label>Timeline</label><select class="pmr-tinp" name="timeline"><option value="">Select</option><option>This month</option><option>1-3 months</option><option>Just exploring</option></select></div></div>' +
      // honeypot: off-screen, not display:none (bots skip display:none)
      '<div style="position:absolute;left:-9999px;top:-9999px" aria-hidden="true"><label>Website<input name="website" tabindex="-1" autocomplete="off"></label></div>' +
      '<button class="pmr-btn pri" type="submit" id="pmr-submit">Send My Plan</button>' +
      '<p class="pmr-center-fine">Real panels, real colors, real price. Full pricing in your proposal.</p>' +
      '<div id="pmr-err" class="pmr-err" style="display:none"></div>' +
      '</form></div>';
  }

  function screenS5() {
    var swatchUrl = CFG.swatchUrl || '/pages/free-room-analysis';
    // LAUNCH-GATED app upsell (2026-07-13): renders ONLY when APP_STORE_URL is set (see the
    // flip-procedure comment at APP_STORE_URL, near FRA_URL above). The app is not live yet,
    // so this stays empty and zero app mentions render anywhere in the flow. The swatch + FRA
    // options below are the honest "want it exact" paths until launch.
    var appCard = APP_STORE_URL
      ? ('<div class="pmr-upsell"><div class="t">Want it exact?</div>' +
         '<div class="d">Scan your room with your iPhone to see the exact layout and the panels in your real room. The premium upgrade, not a requirement.</div>' +
         '<a class="pmr-btn pri" style="box-shadow:none" href="' + APP_STORE_URL + '">Get the exact scan. Get the app.</a></div>')
      : '';
    return NAV + '<div class="pmr-scr">' +
      '<div class="pmr-conf">✓</div>' +
      '<h3 style="text-align:center">Your plan to quiet the room is on its way.</h3>' +
      '<p class="pmr-lede" style="text-align:center">A specialist follows up with final pricing, usually within a day.</p>' +
      appCard +
      '<a class="pmr-btn sec" href="' + FRA_URL + '">Prefer an expert to take it from here? Free Room Analysis</a>' +
      '<a class="pmr-btn sec" href="' + swatchUrl + '">Order a fabric swatch sample</a>' +
      '<a class="pmr-btn ghost" href="tel:+18889235777">Call ' + PHONE + '</a>' +
      '</div>';
  }

  function helpBox(text) {
    return '<div class="pmr-help" ' + (st.help ? '' : 'hidden') + '>' + text + '</div>';
  }

  // ======================= RENDER + WIRE =======================
  function render() {
    // Steps: 0 fork · 1 space · 2 dims+problem · 3 surfaces (Phase 2) · 4 plan
    // · 5 send form · 6 done.
    var html;
    if (st.step === 0) html = screenS0();
    else if (st.step === 1) html = screenS1();
    else if (st.step === 2) html = screenS2();
    else if (st.step === 3) html = screenSurf();
    else if (st.step === 4) html = screenS3();
    else if (st.step === 5) html = screenS4();
    else html = screenS5();
    ROOT.innerHTML = '<div class="pmr-phone">' + html + '</div>';
    wire();
    ROOT.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }

  function setStep(n) { st.step = n; st.help = false; render(); }

  function wire() {
    // help toggle
    var hb = $('[data-help]'); if (hb) hb.addEventListener('click', function () { st.help = !st.help; var box = $('.pmr-help'); if (box) box.hidden = !st.help; });
    // go buttons
    $all('[data-go]').forEach(function (b) {
      b.addEventListener('click', function (e) {
        var g = b.getAttribute('data-go');
        if (g === 's1') { trackFork('diy'); setStep(1); }
        else if (g === 'surfaces') setStep(3);
        else if (g === 'plan') setStep(4);
        else if (g === 's4') setStep(5);
        else if (g === 'cart') addToCart();
        else if (g === 'emailcopy') emailCopy();
        else if (g === 'share') sharePlan(b);
        else if (g === 's5') setStep(6);
      });
    });
    // Phase 2: problem chips (tap to select, tap again to clear; bias only
    // upgrades the suggested tier)
    $all('[data-problem]').forEach(function (b) {
      b.addEventListener('click', function () {
        var k = b.getAttribute('data-problem');
        st.problem = st.problem === k ? null : k;
        st.tier = tierWithProblemBias();
        render(); syncEdit();
      });
    });
    // Phase 2: surface picker wall toggles (SVG hit rects)
    $all('[data-wall]').forEach(function (r) {
      r.addEventListener('click', function () {
        var id = r.getAttribute('data-wall');
        st.skipWalls[id] = !st.skipWalls[id];
        resetAdjustments();
        render(); syncEdit();
      });
    });
    // Phase 2: ceiling switch
    $all('[data-ceil]').forEach(function (b) {
      b.addEventListener('click', function () {
        st.ceilingOn = !st.ceilingOn;
        resetAdjustments();
        render(); syncEdit();
      });
    });
    // Phase 2: layout tabs
    $all('[data-wtab]').forEach(function (b) {
      b.addEventListener('click', function () {
        st.selWall = b.getAttribute('data-wtab');
        render();
      });
    });
    // Phase 2: per-surface +/- (capacity-bounded; engine reclamps regardless)
    $all('[data-adj]').forEach(function (b) {
      b.addEventListener('click', function () {
        var tabId = b.getAttribute('data-adj'), d = Number(b.getAttribute('data-d'));
        var p = st.plan || computePlan();
        if (tabId === 'ceiling') {
          var curC = p.ceilingCount;
          st.ceilCount = clampNum(curC + d, 0, p.ceilingCap);
        } else {
          var wi = null;
          for (var i = 0; i < p.wallInfo.length; i++) { if (p.wallInfo[i].id === tabId) { wi = p.wallInfo[i]; break; } }
          if (!wi) return;
          st.wallCounts[tabId] = clampNum(wi.count + d, 0, wi.cap);
        }
        st.selWall = tabId;
        render(); syncEdit();
      });
    });
    // Phase 2: reset customer count adjustments
    $all('[data-resetlayout]').forEach(function (b) {
      b.addEventListener('click', function () { resetAdjustments(); render(); syncEdit(); });
    });
    // S0 expert card -> quiet_fork_click (source:'planner', path:'expert'); the link still navigates
    $all('[data-fork]').forEach(function (b) {
      b.addEventListener('click', function () { trackFork(b.getAttribute('data-fork')); });
    });
    // space tiles -> select + advance
    $all('[data-space]').forEach(function (b) {
      b.addEventListener('click', function () {
        selectSpace(b.getAttribute('data-space'));
        setStep(2);
      });
    });
    // dim steppers — click to nudge, HOLD to run, or tap the value to type it.
    // Rage fix (2026-08-27): +/- moved 1ft a click across a 5-200ft range and
    // there was no typed entry, so reaching a real room size cost 20-180 clicks.
    // Friction analytics logged 257 rage-click bursts on .pmr-stepb over 154
    // sessions; one visitor clicked "+" 102 times (25ft -> 47ft at ~200ms a click).
    // These handlers deliberately do NOT call render(): the surgical span update
    // keeps a held repeat smooth and never tears down a value being typed into.

    // Slice 3.1: the engine owns the dimension limits (PLAN_DIM_LIMITS) so a
    // shared link can never restore a room these steppers would refuse to
    // type. Literals stay only as the pre-3.1-bundle fallback.
    function dimLimits(key) {
      return (E && E.PLAN_DIM_LIMITS && E.PLAN_DIM_LIMITS[key]) || (key === 'H' ? [6, 40] : [5, 200]);
    }
    function paintDim(key) {
      var full = document.getElementById('pmr-' + key);
      if (full) full.innerHTML = st[key] + ' <span class="u">ft</span>';
      var mini = document.getElementById('pmr-mini-' + key); if (mini) mini.textContent = st[key];
    }
    function setDim(key, val) {
      var lim = dimLimits(key), next = clampNum(val, lim[0], lim[1]);
      if (next === st[key]) return false; // already at a limit / no change
      st[key] = next;
      resetAdjustments(); // a different room invalidates per-wall count edits
      paintDim(key);
      return true;
    }
    $all('[data-dim]').forEach(function (b) {
      var key = b.getAttribute('data-dim'), d = Number(b.getAttribute('data-d'));
      var holdT = null, repT = null, ticks = 0, ran = false;
      function stopHold() {
        if (holdT) { clearTimeout(holdT); holdT = null; }
        if (repT) { clearInterval(repT); repT = null; }
        ticks = 0;
      }
      b.addEventListener('click', function () {
        if (ran) { ran = false; return; } // the hold already moved it
        setDim(key, st[key] + d);
      });
      b.addEventListener('pointerdown', function (ev) {
        if (ev.button && ev.button !== 0) return; // left / touch / pen only
        stopHold();
        holdT = setTimeout(function () {
          repT = setInterval(function () {
            ran = true; // only once a tick really fired, so a short press still counts
            ticks++;
            var by = ticks > 20 ? 5 : (ticks > 8 ? 2 : 1); // accelerate on a long hold
            if (!setDim(key, st[key] + d * by)) stopHold(); // hit the limit
          }, 60);
        }, 350);
      });
      ['pointerup', 'pointerleave', 'pointercancel'].forEach(function (evt) {
        b.addEventListener(evt, stopHold);
      });
    });
    // Tap the value to type an exact size. Visitors already clicked this span
    // expecting an input (rage bursts landed on span.pmr-v / div.pmr-inp / span.u)
    // and it had no handler at all.
    $all('[data-dimval]').forEach(function (sp) {
      var key = sp.getAttribute('data-dimval');
      function openEditor() {
        if (sp.querySelector('input')) return; // already editing
        var lim = dimLimits(key);
        sp.innerHTML = '<input class="pmr-vin" type="number" inputmode="numeric" pattern="[0-9]*"' +
          ' min="' + lim[0] + '" max="' + lim[1] + '" value="' + st[key] + '"' +
          ' aria-label="Size in feet, ' + lim[0] + ' to ' + lim[1] + '"> <span class="u">ft</span>';
        var inp = sp.querySelector('input'), done = false;
        function commit() {
          if (done) return; done = true;
          var raw = parseInt(inp.value, 10);
          if (!isNaN(raw)) setDim(key, raw);
          paintDim(key); // always restore the plain value display
        }
        inp.addEventListener('blur', commit); // tap/click away commits too
        inp.addEventListener('keydown', function (ev) {
          // commit directly rather than via blur(): blur only fires if this input
          // is still the focused element, which is not guaranteed on mobile.
          if (ev.key === 'Enter') { ev.preventDefault(); ev.stopPropagation(); commit(); }
          else if (ev.key === 'Escape') { ev.preventDefault(); ev.stopPropagation(); done = true; paintDim(key); }
        });
        try { inp.focus(); } catch (e) {}
        try { inp.select(); } catch (e) {} // throws on type=number in some browsers
      }
      sp.addEventListener('click', openEditor);
      sp.addEventListener('keydown', function (ev) {
        // keyboard affordance for the span itself only — an Enter that came from
        // the editor input has already committed and must not re-open it.
        if (ev.target !== sp) return;
        if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); openEditor(); }
      });
    });
    // photo inputs
    $all('[data-photo]').forEach(function (inp) {
      inp.addEventListener('change', function () {
        if (inp.files && inp.files[0]) { st.photos[inp.getAttribute('data-photo')] = inp.files[0]; render(); }
      });
    });
    // tier picker (live recompute + submit:false sync)
    $all('[data-tier]').forEach(function (b) {
      b.addEventListener('click', function () { st.tier = b.getAttribute('data-tier'); render(); syncEdit(); });
    });
    // fabric-line picker (reset color to the new line's default)
    $all('[data-fabric]').forEach(function (b) {
      b.addEventListener('click', function () { st.tileKey = b.getAttribute('data-fabric'); st.color = null; render(); syncEdit(); });
    });
    // color picker (index into the currently-shown color list)
    $all('[data-coloridx]').forEach(function (b) {
      b.addEventListener('click', function () {
        var idx = Number(b.getAttribute('data-coloridx'));
        var list = st.plan ? st.plan.colors : null;
        if (list && list[idx]) { st.color = { name: list[idx].name, line: list[idx].line, image: list[idx].image }; render(); syncEdit(); }
      });
    });
    // form submit
    var form = document.getElementById('pmr-form');
    if (form) form.addEventListener('submit', function (e) { e.preventDefault(); sendMyPlan(form); });
  }

  function selectSpace(key) {
    var t = typeByKey(key); if (!t) t = ROOM_TYPES[7];
    st.space = t;
    st.L = t.def[0]; st.W = t.def[1]; st.H = t.def[2];
    // Fresh space = fresh Phase-2 state (surfaces, adjustments, problem chip),
    // then the tier from the space type + any (now cleared) problem bias.
    st.skipWalls = {}; st.ceilingOn = true; st.problem = null;
    resetAdjustments();
    st.tier = tierWithProblemBias();
  }

  // ---- Build the intake payload + parts ----
  function buildPayload(submit) {
    var p = st.plan || computePlan();
    var reduction = p.reduction;
    var perBand = p.rep.bands.map(function (b) { return { band: b.band, before: b.rt60Before, after: b.rt60After }; });
    var pr = problemByKey(st.problem);
    var skips = skippedWallIds();
    var skippedNames = skips.map(function (id) {
      var idx = Number(String(id).slice(1));
      return NAMES_FULL[idx] || id;
    });
    var placementLabel = (skips.length ? 'Customer-adjusted' : 'Auto-planned') + ' wall layout (' + p.count + ' x ' + p.sizeLabel + ')' +
      (skips.length ? ', skipping ' + skippedNames.join(', ') : '');
    var payload = {
      website: '',                 // honeypot (blank for real submits)
      form_ts: st.formLoadedAt,
      submit: submit === true,
      source: 'webroom_plan_my_room',
      spaceType: st.space ? st.space.key : 'Other',
      problem: pr ? pr.label + ' (customer-selected)' : 'Echo / reverberation control',
      roomLengthFt: st.L, roomWidthFt: st.W, ceilingHeightFt: st.H,
      placement: placementLabel,
      fabric: p.fabricLabel || undefined,
      colorPreference: p.color ? p.color.name : undefined,
      recommendation: {
        tier: st.tier, coveragePct: p.coveragePct, panelCount: p.count,
        panelSize: p.sizeLabel,                                                       // e.g. 24" x 48" x 2"
        largeFormatSuggested: p.isLarge ? ('48" x 96" x ' + st.thickness + '"') : undefined,
        wallSqFt: Math.round(p.wallSqFt), ceilingSqFt: Math.round(p.ceilingSqFt),
        totalSurfaceSqFt: Math.round(p.totalSqFt)
      },
      bom: [{ title: p.count + ' x ' + p.sizeLabel + (p.fabricLabel ? ' (' + p.fabricLabel + (p.color ? ': ' + p.color.name : '') + ')' : ''), variantId: p.variant ? String(p.variant.id) : undefined, qty: p.count, priceCents: p.unitCents }],
      acoustics: {
        modeledBeforeMid: p.rep.rt60BeforeMid,
        expectedAfterMid: p.rep.rt60AfterMid,
        reductionPercent: reduction,
        perBand: perBand,
        panelAreaSqFt: p.count * p.panelFaceSqFt,
        panelProfile: p.profile
      },
      // Phase 2: surfaces + customer adjustments ride the same intake payload
      // (proxy passes through; the specialist sees exactly what the customer
      // constrained and changed).
      surfaces: {
        skippedWallIds: skips,
        skippedWalls: skippedNames,
        ceiling: st.ceilingOn,
        perWall: (p.wallInfo || []).map(function (w) {
          return { wall: NAMES_FULL[w.index] || w.id, id: w.id, count: w.count, capacity: w.cap, skipped: w.skipped };
        }),
        ceilingCount: p.ceilingCount
      }
    };
    if (p.adjusted) {
      payload.adjustments = {
        customerAdjusted: true,
        perWallOverrides: st.wallCounts,
        ceilingOverride: st.ceilCount,
        adjustedTotal: p.count,
        recommendedTotal: p.recommendedCount,
        overflow: p.overflow,
        belowCoverageFloor: p.floorCount > 0 && p.count < p.floorCount
      };
    }
    // Slice 2.3: when the large-format plan is the buyable one, the BOM (the line
    // a cart/order is actually built from) and the tracked deal value follow the
    // 48x96 plan, so Deal.value + the $500 hot-config alert reflect what the
    // customer can buy. The standard recommendation context above is preserved.
    if (largeBuyActive(p)) {
      var lg = p.large;
      payload.sizePreference = 'large';
      payload.largePlan = {
        panelCount: lg.count,
        panelSize: lg.sizeLabel,
        unitCents: lg.unitCents,
        totalCents: lg.pricePoint,
        variantId: lg.variant ? String(lg.variant.id) : undefined,
        freight: 'dock-to-dock; liftgate available as an add-on'
      };
      payload.bom = [{
        title: lg.count + ' x ' + lg.sizeLabel + (p.fabricLabel ? ' (' + p.fabricLabel + (p.color ? ': ' + p.color.name : '') + ')' : ''),
        variantId: lg.variant ? String(lg.variant.id) : undefined,
        qty: lg.count,
        priceCents: lg.unitCents
      }];
    }
    return payload;
  }

  function buildGeometry() {
    var p = st.plan || computePlan();
    // Slice 2.3: track the buyable large-format total as the deal value when the
    // large buy path is active; otherwise the standard plan total.
    var buy = largeBuyActive(p) ? p.large : p;
    return {
      placedTotalPriceCents: buy.pricePoint,
      plan: { panelCount: buy.count, coveragePct: p.coveragePct, tier: st.tier, sizeLabel: buy.sizeLabel, unitCents: buy.unitCents },
      dims: { lengthFt: st.L, widthFt: st.W, heightFt: st.H },
      priceRangeCents: largeBuyActive(p) ? [buy.pricePoint, buy.pricePoint] : [p.priceLow, p.priceHigh]
    };
  }

  function jsonBlob(obj) { return new Blob([JSON.stringify(obj)], { type: 'application/json' }); }

  // ---- Send My Plan (submit:true) ----
  function sendMyPlan(form) {
    var errBox = document.getElementById('pmr-err');
    var btn = document.getElementById('pmr-submit');
    var fd = new FormData(form);
    var firstName = (fd.get('firstName') || '').toString().trim();
    var email = (fd.get('email') || '').toString().trim();
    var website = (fd.get('website') || '').toString().trim();
    if (!firstName || !email || email.indexOf('@') < 0) {
      showErr(errBox, 'Please add your first name and a valid email.'); return;
    }
    if (btn) { btn.disabled = true; btn.textContent = 'Sending...'; }

    var payload = buildPayload(true);
    payload.website = website; // pass through the honeypot value (blank for humans)
    payload.contact = {
      firstName: firstName,
      email: email,
      zip: (fd.get('zip') || '').toString().trim() || undefined,
      phone: (fd.get('phone') || '').toString().trim() || undefined
    };
    payload.timeline = (fd.get('timeline') || '').toString().trim() || undefined;

    var svg = wallLayoutSvg(st.plan);
    var rd = rasterDims(svg);
    svgToPng(svg, rd.w, rd.h).then(function (png) {
      var out = new FormData();
      out.append('payload', JSON.stringify(payload));
      out.append('geometry', jsonBlob(buildGeometry()), 'geometry.json');
      if (png) out.append('render2d', png, 'plan-render2d.png');
      // labeled wall photos (photos-only mode)
      var manifest = [];
      Object.keys(st.photos).forEach(function (label, i) {
        var f = st.photos[label];
        if (f) { var fn = 'wallPhoto-' + (i + 1) + '.jpg'; out.append('wallPhoto', f, fn); manifest.push({ surface: label.toLowerCase().replace(/\s+/g, '-'), label: label, filename: fn }); }
      });
      if (manifest.length) { payload.wallPhotos = manifest; out.set('payload', JSON.stringify(payload)); }

      fetch(CFG.proxyUrl, { method: 'POST', body: out })
        .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, d: d }; }); })
        .then(function (res) {
          if (res.ok && (res.d.dealId || res.d.contactId || res.d.skipped)) {
            st.sent = true; st.contactId = res.d.contactId || st.contactId; st.dealId = res.d.dealId || st.dealId;
            setStep(6);
          } else {
            if (btn) { btn.disabled = false; btn.textContent = 'Send My Plan'; }
            showErr(errBox, (res.d && res.d.error) ? res.d.error : 'That did not go through. Please try again, or call ' + PHONE + '.');
          }
        })
        .catch(function () {
          if (btn) { btn.disabled = false; btn.textContent = 'Send My Plan'; }
          showErr(errBox, 'Network hiccup. Please try again, or call ' + PHONE + '.');
        });
    });
  }

  function showErr(box, msg) { if (box) { box.textContent = msg; box.style.display = 'block'; } }

  // ---- Background sync on tier/color/dims edit (submit:false) ----
  // Only after the first Send My Plan (mockup: "no cold silent sync").
  var syncTimer = null;
  function syncEdit() {
    if (!st.sent) return;
    if (syncTimer) clearTimeout(syncTimer);
    syncTimer = setTimeout(function () {
      var payload = buildPayload(false);
      payload.contact = { firstName: 'edit', email: st._email || '' };
      // reuse the identified email so the intake merges into the open deal
      if (!st._email) return;
      payload.contact.email = st._email;
      var out = new FormData();
      out.append('payload', JSON.stringify(payload));
      out.append('geometry', jsonBlob(buildGeometry()), 'geometry.json');
      fetch(CFG.proxyUrl, { method: 'POST', body: out }).catch(function () {});
    }, 900);
  }

  // ---- Email me a copy (soft rung -> contacts) ----
  function emailCopy() {
    var email = window.prompt('Email your plan summary to:');
    if (!email || email.indexOf('@') < 0) return;
    st._email = email.trim();
    fetch(CFG.proxyUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'email-copy', email: st._email, name: (st.space ? st.space.key : '') + ' room', website: '', form_ts: st.formLoadedAt })
    }).then(function (r) { return r.json(); }).then(function () {
      alert('Sent. Check your inbox for your plan summary.');
    }).catch(function () { alert('Could not send right now. Please try Send My Plan instead.'); });
  }

  // ---- Add to Cart (secondary; REAL engine-resolved variant + cart props) ----
  function addToCart() {
    var p = st.plan || computePlan();
    // Phase 2 guard (defense in depth; the exits UI already replaces the cart
    // CTA): never post a cart the theme minimum-quantity rules will reject at
    // checkout. Steer to Send My Plan instead.
    if (cartBlockReason(p)) { setStep(5); return; }
    // Slice 2.3: a buyable large room buys the real 48x96 large-format plan
    // (correct variant + count + price); every other plan buys the standard face.
    var buy = largeBuyActive(p) ? p.large : p;
    if (!buy.variant || !buy.variant.id || !buy.isPurchasable) { window.location.href = CFG.configuratorUrl || '/pages/configurator'; return; }
    var props = {};
    (buy.cartAttributes || []).forEach(function (a) { props[a.key] = a.value; });
    fetch('/cart/add.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(buy.variant.id), quantity: buy.count, properties: props }] })
    }).then(function (r) {
      if (r.ok) { window.location.href = '/cart'; }
      else { window.location.href = CFG.configuratorUrl || '/pages/configurator'; }
    }).catch(function () { window.location.href = CFG.configuratorUrl || '/pages/configurator'; });
  }

  // ---- Load the LIVE v:2 catalog island (assets/asp-configurator-data.json,
  // kept fresh by the ASP rebake cron) and decode via the engine. Async — counts
  // + RT60 never need it; only price does, and the user is several taps from S3. ----
  function loadCatalog() {
    if (!CFG.catalogUrl) return Promise.resolve();
    return fetch(CFG.catalogUrl)
      .then(function (r) { return r.text(); })
      .then(function (t) { CATALOG = E.decodeCatalog(JSON.parse(t)); })
      .catch(function (e) { console.error('[plan-my-room] catalog load failed (price falls back to reference):', e); CATALOG = null; });
  }

  // ---- Load the PDP swatch feed (assets/fabric-color-data.json) for real color
  // names + swatch images. Async — only the S3 fabric card needs it; counts/RT60
  // never do. On failure the picker falls back to the catalog palette. ----
  function loadFabricColors() {
    if (!CFG.fabricColorUrl) return Promise.resolve();
    return fetch(CFG.fabricColorUrl)
      .then(function (r) { return r.json(); })
      .then(function (j) { FCD = (j && j.colors) ? j : null; _fcdIdx = null; })
      .catch(function (e) { console.error('[plan-my-room] fabric colors load failed (falls back to palette):', e); FCD = null; });
  }

  // ---- Boot: shared-plan restore, then ?type= pre-fill, then render; catalog
  //      loads in background ----
  function boot() {
    try {
      var q = new URLSearchParams(window.location.search);
      // Slice 3.1: a shared plan wins over every other deep link. The recipient
      // asked for THIS room, so land them straight on the plan screen (step 4)
      // rather than walking them back through inputs they did not fill in. A
      // token we cannot read falls through to the normal funnel start — never a
      // half-restored room.
      var shared = null;
      if (shareReady()) {
        try { shared = E.decodeSharedPlan(q.get(E.SHARE_PARAM || 'p')); } catch (e0) { shared = null; }
      }
      if (shared) {
        applySharedPlan(shared);
        st.step = 4;
        try { if (typeof gtag === 'function') { gtag('event', 'plan_share_open', { source: 'planner', space: shared.spaceType }); } } catch (e1) {}
      }
      var type = shared ? null : q.get('type');
      if (type) { var t = typeByKey(type); if (t) { selectSpace(t.key); st.step = 2; } }
      // ?start=1 : the homepage "Do it myself" fork card deep-links straight to the
      // room-type step (S1), skipping the S0 fork so homepage clickers never see the
      // fork twice. Guard: only skip when we have NOT already advanced past S0 via
      // ?type= above, so a ?type=studio&start=1 link still honors the pre-selected type.
      if (st.step === 0 && q.get('start') === '1') { st.step = 1; }
    } catch (e2) {}
    render();
    // Re-render when the catalog/color islands land IF the user is already on a
    // priced screen (3 = surfaces live-strip, 4 = the plan).
    Promise.all([loadCatalog(), loadFabricColors()]).then(function () { if (st.step === 3 || st.step === 4) render(); });
  }
  // capture email typed into the form for the edit-sync merge key
  ROOT.addEventListener('input', function (e) {
    if (e.target && e.target.name === 'email') st._email = e.target.value.trim();
  });

  boot();
})();
